import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CalendarPlus,
  ChevronDown,
  FileArchive,
  FileText,
  MessageSquare,
  Pencil,
  Plus,
  Printer,
  Trash2,
  Target,
} from 'lucide-react';
import TaskDocumentsPopover from '../components/documents/TaskDocumentsPopover';
import PatientAllergies from '../components/PatientAllergies';
import { EventsTab } from '../components/patient/EventsTab';
import { AuthorizationsTab } from '../components/patient/AuthorizationsTab';
import GoalsTab from '../components/goals/GoalsTab';
import { PatientWounds } from '../components/wounds';
import {
  getPatient,
  getPatientTasks,
  createPatientVisit,
  createOrder,
  updateOrder,
  deleteOrder,
  getAvailableClinicians,
  getPatientWounds,
  getPatientDocuments,
  getIntakes,
  updateVisit,
  deleteVisit,
  getPatientAllergies,
  getPatientMedications,
  listPatientCommunications,
  updatePatientCommunication,
  deletePatientCommunication,
  createPatientCommunication,
  createMiscTask,
  updateMiscTask,
  deleteMiscTask,
  getCurrentEpisode,
  listAgencyClinicPhysicians,
  listEpisodesForPatient,
  batchPrintEpisodeTasks,
  listPatientInsurances,
  getAuthorizationErrors,
  updateEpisodeAssignments,
  getEpisodeLupa,
  type EpisodeLupaPeriodResponse,
} from '../services/api';
import { useStore } from '../store';
import PatientDocumentsTab from '../components/documents/PatientDocumentsTab';
import VisitsCalendarPanel from '../components/VisitsCalendarPanel';
import ScheduleVisitsDialog from '../components/ScheduleVisitsDialog';
import type { PatientDocumentsTabHandle } from '../components/documents/PatientDocumentsTab';
import { PatientMedications } from '../components/medications';
import type {
  PatientResponse,
  PatientInsuranceResponse,
  TaskListResponse,
  AvailableClinicianResponse as AvailableClinician,
  ClinicPhysicianResponse,
  CurrentEpisodeResponse,
  EpisodeSummaryResponse,
  PatientCommunicationResponse,
  OrderResponse,
  OrderTypeId,
  PatientCommunicationTypeId,
  MiscTaskTypeId,
  VisitTypeId,
  AuthorizationError,
} from '../services/api/types.gen';
import { formatPhysicianName } from '../utils/formatting';
import { useVisitTypeOptions } from '../hooks/useVisitTypeOptions';
import { useToast } from '../hooks/useToastContext';
import { hasPrintView, usePrintView } from '../hooks/usePrintView';
import { usePdfExport } from '../hooks/usePdfExport';
import { useActiveGoalCount } from '../hooks/useActiveGoalCount';
import { useIsMobile } from '../hooks/useIsMobile';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  formatDateToMMDDYYYY,
  formatDateToISO,
  getTodayInPT,
  parseISOToLocalDate,
  sortByLocalDate,
} from '../utils/dateUtils';
import {
  clinicianMatchesDiscipline,
  cliniciansToDropdownOptions,
  getAssigneeDisciplineForTask,
  getAssigneeDisciplineForTaskType,
  getClinicianDisplayName as formatClinicianName,
  type ClinicianDropdownOption,
  type ClinicianSelectInput,
} from '../utils/clinicianUtils';
import { getBusinessErrorMessage } from '../utils/apiErrorDetail';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  cn,
  CardHeader,
  CardDescription,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownField,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { PatientHeader } from '../components/patient/PatientHeader';
import { PatientInfoTab } from '../components/patient/PatientInfoTab';
import { PatientInsuranceFormDialog } from '../components/patient/PatientInsuranceFormDialog';
import { ThirtyDaySummaryDetailPanel } from '../components/patient/ThirtyDaySummaryDetailPanel';
import { THIRTY_DAY_SUMMARY_COMMUNICATION_TYPE } from '../components/patient/patientCommunicationTypes';
import { CommunicationDetailPanel } from '../components/patient/CommunicationDetailPanel';
import { MiscTaskDetailPanel } from '../components/patient/MiscTaskDetailPanel';
import EpisodeEditor from '../components/episode/EpisodeEditor';
import EpisodeDateSyncDialog from '../components/episode/EpisodeDateSyncDialog';
import { OutsideEpisodeBadge } from '../components/episode/OutsideEpisodeBadge';
import {
  boundaryRescheduleBounds,
  classifyEpisodeBoundaryReschedule,
  type EpisodeBoundary,
} from '../utils/episodeBoundaryVisits';
import { OASIS_VISIT_TYPE_LABELS } from '../constants/oasisVisitTypes';
import { getHttpStatus } from '../utils/httpErrorUtils';
import { EpisodeInsuranceSelector } from '../components/episode/EpisodeInsuranceSelector';
import { EpisodeWorksheetEditor } from '../components/episode/EpisodeWorksheetEditor';
import { VisitDetailPanel } from '../components/visit/VisitDetailPanel';
import { OrderDetailPanel } from '../components/order/OrderDetailPanel';
import AddEpisodeModal from '../components/AddEpisodeModal';
import { LockedTaskIndicator } from '../components/task/LockedTaskIndicator';
import {
  STATUS_DISPLAY_LABELS,
  getStatusBadgeVariant,
} from '../components/task/TaskDetailPanel';
import {
  getScheduleLockedTask,
  isDocumentMutationLocked,
  type ScheduleLockedTask,
} from '../components/task/lockedTask';
import SelectionActionBar, {
  type ConfirmSpec,
  type SelectionAction,
} from '../components/SelectionActionBar';
import {
  deleteConflictCopy,
  partitionDeletable,
  partitionNotStarted,
  partitionPrintable,
  buildPrintConfirm,
  taskManifestGroup,
  toBatchPrintTaskRefs,
} from '../components/task/batchActions';
import {
  TASK_TYPE_DISCIPLINE_MAP,
  showsGoalsSection,
  isOrderType,
  supportsActiveDiagnosisPage,
  supportsWounds,
  type TaskTypeId,
} from '../constants/visitTypes';
import {
  createDefaultTaskTypeFilter,
  createCreatableTaskTypeOptions,
  DEFAULT_NEW_TASK_TYPE,
  filterCreatableTaskTypeOptions,
  filterTaskRows,
  getDefaultCreatableTaskTypeOptionId,
  isVisitTaskRow,
  type CreatableTaskTypeOption,
  type TaskTypeFilterValue,
} from '../components/task/taskTypeFilters';
import { TaskTypeFilter } from '../components/task/TaskTypeFilter';
import { TaskTypeCombobox } from '../components/task/TaskTypeCombobox';
import { BillingManagerLink } from '../components/billing/BillingManagerLink';
import { ClinicianSelect } from '../components/clinician/ClinicianSelect';

const LoadingSpinner: React.FC<{ size?: 'sm' | 'md'; text?: string }> = ({
  size = 'md',
  text,
}) => {
  const sizeClass = size === 'sm' ? 'h-3 w-3' : 'h-8 w-8';
  return (
    <span className='flex items-center gap-1'>
      <div
        className={`animate-spin rounded-full border-b-2 border-primary ${sizeClass}`}
      />
      {text && <span>{text}</span>}
    </span>
  );
};

const PATIENT_TABS = [
  'visits',
  'documents',
  'medications',
  'allergies',
  'wounds',
  'goals',
  'authorizations',
  'events',
  'info',
] as const;
type PatientTab = (typeof PATIENT_TABS)[number];

const LOCKED_TASK_ASSIGNEE_TEXT_CLASS =
  'flex h-7 min-w-0 items-center px-2 text-xs text-muted-foreground';

const LOCKED_TASK_DATE_TEXT_CLASS =
  'flex h-6 max-w-[220px] items-center pr-1.5 text-xs text-muted-foreground';

// Mirrors the backend's MAX_BATCH_PRINT_TASKS ceiling. The frontend caps
// selection here so the 422 backstop is never the primary UX.
const MAX_BATCH_SELECTED = 30;

type AddTaskTargetEpisode = {
  id: string;
  start_date?: string | null;
  end_date?: string | null;
  agency_clinic_id?: string | null;
  episode_physician_id?: string | null;
};

const currentEpisodeToAddTaskTarget = (
  episode: CurrentEpisodeResponse | null
): AddTaskTargetEpisode | null => {
  if (!episode?.episode_id) return null;
  return {
    id: episode.episode_id,
    start_date: episode.start_date,
    end_date: episode.end_date,
    agency_clinic_id: episode.agency_clinic_id,
    episode_physician_id: episode.physician_id,
  };
};

const clampDateYmdToEpisode = (
  candidateYmd: string,
  episode: AddTaskTargetEpisode | null
): string => {
  if (!candidateYmd || !episode) return candidateYmd;
  if (episode.start_date && candidateYmd < episode.start_date) {
    return episode.start_date;
  }
  if (episode.end_date && candidateYmd > episode.end_date) {
    return episode.end_date;
  }
  return candidateYmd;
};

const dateFromYmd = (ymd: string): Date | undefined => parseISOToLocalDate(ymd);

function isValidTab(value: string | undefined): value is PatientTab {
  return PATIENT_TABS.includes(value as PatientTab);
}

/** Tab shown on first paint from URL (visit detail route takes precedence). */
function initialTabFromUrl(params: {
  visitId?: string;
  tab?: string;
}): PatientTab {
  if (params.visitId) return 'visits';
  if (isValidTab(params.tab)) return params.tab;
  return 'visits';
}

/**
 * Which active-episode boundary this task's visit currently sits on, or null.
 * Used both to offer moving the episode date on reschedule and to relax the
 * date picker so a boundary visit can be moved outside the current window.
 */
const activeEpisodeBoundaryForVisit = (
  task: TaskListResponse
): EpisodeBoundary | null => {
  if (task.episode_status !== 'active') {
    return null;
  }
  return classifyEpisodeBoundaryReschedule({
    visitType: task.visit_type,
    currentScheduledDate: task.scheduled_date,
    episodeStartDate: task.episode_start_date,
    episodeEndDate: task.episode_end_date,
  });
};

/**
 * Whether a visit task's type has a wound / goals section. `visit_type` is typed
 * as a loose string on the task payload, so narrow it before consulting the
 * capability lists: an unset or unrecognized value means "no section" rather
 * than a crash.
 */
const taskSupportsWounds = (task: TaskListResponse): boolean =>
  !!task.visit_type && supportsWounds(task.visit_type as VisitTypeId);

const taskShowsGoalsSection = (task: TaskListResponse): boolean =>
  !!task.visit_type && showsGoalsSection(task.visit_type as VisitTypeId);

const PatientDetail: React.FC = () => {
  const {
    id,
    tab: tabParam,
    visitId: visitIdParam,
  } = useParams<{
    id: string;
    tab?: string;
    visitId?: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const episodeIdFromUrl = searchParams.get('episode');
  const sectionParam = searchParams.get('section');
  const { success: showSuccess, error: showError } = useToast();
  const { options: visitTypeOptions } = useVisitTypeOptions();
  const { printTask, printingTaskId } = usePrintView();
  // Drives the synchronous-tab-open batch "Combined PDF" export.
  const { exportPdf: exportBatchPdf } = usePdfExport();
  const isMobile = useIsMobile();
  const [patient, setPatient] = useState<PatientResponse | null>(null);
  const [patientInsurances, setPatientInsurances] = useState<
    PatientInsuranceResponse[]
  >([]);
  const [insurancesLoading, setInsurancesLoading] = useState(true);
  const [insurancePopupOpen, setInsurancePopupOpen] = useState(false);
  const [editingInsuranceId, setEditingInsuranceId] = useState<string | null>(
    null
  );
  const [insuranceRefreshTrigger, setInsuranceRefreshTrigger] = useState(0);
  const [tasks, setTasks] = useState<TaskListResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [visitsLoading, setVisitsLoading] = useState(true);
  const [tasksPatientId, setTasksPatientId] = useState<string | null>(null);
  const [tasksRefreshVersion, setTasksRefreshVersion] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [visitsError, setVisitsError] = useState<string | null>(null);
  const [communications, setCommunications] = useState<
    PatientCommunicationResponse[]
  >([]);
  const [isEpisodeEditorOpen, setIsEpisodeEditorOpen] = useState(false);
  const [isEpisodeInsuranceEditorOpen, setIsEpisodeInsuranceEditorOpen] =
    useState(false);
  const [isEpisodeWorksheetOpen, setIsEpisodeWorksheetOpen] = useState(false);
  const [clinicians, setClinicians] = useState<AvailableClinician[]>([]);
  const [physiciansByClinic, setPhysiciansByClinic] = useState<
    Record<string, ClinicPhysicianResponse[]>
  >({});
  const documentsRef = useRef<PatientDocumentsTabHandle>(null);
  const communicationsFetchedRef = useRef(false);
  const loadedCommunicationTaskLabelsKeyRef = useRef<string | null>(null);
  const [updatingTaskId, setUpdatingTaskId] = useState<string | null>(null);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isScheduleVisitsOpen, setIsScheduleVisitsOpen] = useState(false);
  const addTaskOptions = useMemo(
    () => createCreatableTaskTypeOptions(visitTypeOptions),
    [visitTypeOptions]
  );
  const [selectedAddTaskOptionId, setSelectedAddTaskOptionId] = useState<
    CreatableTaskTypeOption['id']
  >(DEFAULT_NEW_TASK_TYPE);
  const [tableTaskTypeFilter, setTableTaskTypeFilter] =
    useState<TaskTypeFilterValue>(() => createDefaultTaskTypeFilter());
  const [addTaskTypeFilter, setAddTaskTypeFilter] =
    useState<TaskTypeFilterValue>(() => createDefaultTaskTypeFilter());
  const [newTaskAssignee, setNewTaskAssignee] = useState<string>('');
  const [newTaskDate, setNewTaskDate] = useState<Date | undefined>(() =>
    dateFromYmd(getTodayInPT())
  );
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [newCommNotes, setNewCommNotes] = useState('');
  const [newTaskPhysicianId, setNewTaskPhysicianId] = useState('');
  const [currentEpisodeFallbackState, setCurrentEpisodeFallbackState] =
    useState<{
      patientId: string;
      episode: AddTaskTargetEpisode | null;
    } | null>(null);
  const previousPatientIdRef = useRef(id);
  const currentPatientIdRef = useRef(id);
  const taskRequestIdRef = useRef(0);
  const communicationRequestIdRef = useRef(0);
  const pendingAddTaskFilterRef = useRef<TaskTypeFilterValue | null>(null);
  currentPatientIdRef.current = id;

  const [deleteTaskId, setDeleteTaskId] = useState<string | null>(null);
  const [isDeletingTask, setIsDeletingTask] = useState(false);

  // A boundary-defining visit (SOC/REC or DC/TRN/DTH) sitting on an episode date
  // was moved off it, so we offer to move the matching episode date to match.
  const [episodeDateSync, setEpisodeDateSync] = useState<{
    boundary: EpisodeBoundary;
    episodeId: string;
    visitId: string;
    newDate: string;
    currentBoundaryDate: string;
    episodeStartDate: string | null;
    episodeEndDate: string | null;
    visitLabel?: string;
  } | null>(null);

  // Batch-actions selection for the episode task table (ROG-2506). Capped at
  // MAX_BATCH_SELECTED; cleared whenever the active episode changes (the table
  // does not remount on episode switch — see the effect after selectedEpisode).
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(
    () => new Set()
  );

  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(
    visitIdParam ?? null
  );
  const [selectedEpisodeId, setSelectedEpisodeId] = useState<string | null>(
    null
  );
  const [episodes, setEpisodes] = useState<EpisodeSummaryResponse[]>([]);
  const [lupaPeriods, setLupaPeriods] = useState<EpisodeLupaPeriodResponse[]>(
    []
  );
  const [canAddEpisode, setCanAddEpisode] = useState(false);
  const [addEpisodeOpen, setAddEpisodeOpen] = useState(false);
  // Active tab for main content area — synced with URL
  const [activeTab, setActiveTabState] = useState<PatientTab>(() =>
    initialTabFromUrl({
      visitId: visitIdParam,
      tab: tabParam,
    })
  );
  const communicationTaskIdsKey = useMemo(
    () =>
      tasks
        .filter(task => task.task_type === 'patient_communication')
        .map(task => task.id)
        .sort()
        .join('|'),
    [tasks]
  );

  const setActiveTab = (tab: PatientTab) => {
    setActiveTabState(tab);
    if (!id) return;
    let path: string;
    const selectedNavigationTask = tasks.find(
      task => task.id === selectedVisitId
    );
    if (
      (tab === 'wounds' || tab === 'goals') &&
      selectedNavigationTask &&
      selectedNavigationTask.task_type !== 'visit'
    ) {
      setSelectedVisitId(null);
    }
    if (
      tab === 'visits' &&
      selectedNavigationTask &&
      selectedNavigationTask.task_type !== 'patient_communication'
    ) {
      path = `/patients/${id}/visits/${selectedVisitId}`;
    } else if (tab === 'visits') {
      setSelectedVisitId(null);
      path = `/patients/${id}`;
    } else {
      path = `/patients/${id}/${tab}`;
    }
    navigate(path, { replace: true });
  };

  useEffect(() => {
    if (visitIdParam) {
      setSelectedVisitId(visitIdParam);
      setActiveTabState('visits');
      return;
    }
    const resolved = isValidTab(tabParam) ? tabParam : 'visits';
    setActiveTabState(resolved);
    if (resolved === 'visits') {
      setSelectedVisitId(null);
    }
  }, [tabParam, visitIdParam]);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setInsurancesLoading(false);
      return;
    }

    // Clear patient-scoped state before fetching so stale insurances/episodes
    // from the previous patient cannot bleed into PatientHeader.
    setPatient(null);
    setPatientInsurances([]);
    setInsurancesLoading(true);
    setEpisodes([]);
    setCanAddEpisode(false);
    setCurrentEpisodeFallbackState(null);
    setLoading(true);
    setError(null);

    const fetchPatient = async () => {
      const patientId = id;

      try {
        const patientData = await getPatient({
          path: { patient_id: patientId },
        });
        if (currentPatientIdRef.current !== patientId) {
          return;
        }
        if (patientData && patientData.data) {
          setPatient(patientData.data);
        }
      } catch (err) {
        if (currentPatientIdRef.current !== patientId) {
          return;
        }
        console.error('Error fetching patient:', err);
        setError('Failed to load patient details');
      } finally {
        if (currentPatientIdRef.current === patientId) {
          setLoading(false);
        }
      }
    };

    const fetchPatientInsurances = async () => {
      const patientId = id;
      try {
        const res = await listPatientInsurances({
          path: { patient_id: patientId },
        });
        if (currentPatientIdRef.current !== patientId) {
          return;
        }
        if (res.data) setPatientInsurances(res.data);
      } catch (err) {
        if (currentPatientIdRef.current !== patientId) {
          return;
        }
        console.error('Failed to load patient insurances:', err);
      } finally {
        if (currentPatientIdRef.current === patientId) {
          setInsurancesLoading(false);
        }
      }
    };

    fetchPatient();
    fetchPatientInsurances();
  }, [id]);

  const fetchTasks = useCallback(
    async (options?: { silent?: boolean }) => {
      // A silent refresh reloads task data without flipping the section into its
      // loading state, so editing a single task (date/clinician) updates the row
      // in place instead of flashing the whole visits section away and back.
      const silent = options?.silent ?? false;
      if (!id) return;
      const patientId = id;
      const requestId = taskRequestIdRef.current + 1;
      taskRequestIdRef.current = requestId;
      const isLatestTaskRequest = () =>
        currentPatientIdRef.current === patientId &&
        taskRequestIdRef.current === requestId;

      try {
        if (!silent) {
          setVisitsLoading(true);
          setVisitsError(null);
        }
        const tasksData = await getPatientTasks({
          path: { patient_id: patientId },
        });
        if (!isLatestTaskRequest()) {
          return;
        }
        if (tasksData?.data) {
          setTasks(tasksData.data);
          setTasksPatientId(patientId);
          setTasksRefreshVersion(version => version + 1);
          return true;
        }
        return false;
      } catch (err) {
        if (!isLatestTaskRequest()) {
          return;
        }
        console.error('Error fetching visits/tasks:', err);
        if (!silent) {
          setVisitsError('Failed to load tasks');
        }
        return false;
      } finally {
        // Always clear the loading flag for the latest request, even on a
        // silent fetch: a silent refresh can supersede a non-silent one that
        // set visitsLoading, and it must take over clearing it. Clearing when
        // already false is an idempotent no-op.
        if (isLatestTaskRequest()) {
          setVisitsLoading(false);
        }
      }
    },
    [id]
  );

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const loadEpisodes = useCallback(async () => {
    if (!id) return;
    const patientId = id;

    try {
      const response = await listEpisodesForPatient({
        path: { patient_id: patientId },
      });
      if (currentPatientIdRef.current !== patientId) {
        return;
      }
      setEpisodes(response.data?.episodes ?? []);
      setCanAddEpisode(response.data?.can_add_episode ?? false);
    } catch (err) {
      if (currentPatientIdRef.current !== patientId) {
        return;
      }
      console.error('Error fetching episodes:', err);
      setEpisodes([]);
      setCanAddEpisode(false);
    }
  }, [id]);

  useEffect(() => {
    void loadEpisodes();
  }, [loadEpisodes]);

  useEffect(() => {
    if (!episodeIdFromUrl || episodes.length === 0) {
      return;
    }
    if (episodes.some(episode => episode.id === episodeIdFromUrl)) {
      setSelectedEpisodeId(episodeIdFromUrl);
    }
  }, [episodeIdFromUrl, episodes]);

  useEffect(() => {
    communicationsFetchedRef.current = false;
    loadedCommunicationTaskLabelsKeyRef.current = null;
    setTasksPatientId(null);
    setTasksRefreshVersion(0);
    setCommunications([]);
  }, [id]);

  useEffect(() => {
    const loadCommunications = async () => {
      if (!id) return;
      const patientId = id;
      const taskRowsReady = tasksPatientId === id;
      const needsCommunicationLabels = communicationTaskIdsKey !== '';
      const communicationTaskLabelsKey =
        taskRowsReady && needsCommunicationLabels
          ? `${communicationTaskIdsKey}:${tasksRefreshVersion}`
          : null;
      const communicationTaskLabelsStale =
        communicationTaskLabelsKey !== null &&
        loadedCommunicationTaskLabelsKeyRef.current !==
          communicationTaskLabelsKey;
      const shouldLoadForTaskLabels =
        communicationTaskLabelsStale && needsCommunicationLabels;

      if (!shouldLoadForTaskLabels) {
        if (taskRowsReady && !needsCommunicationLabels) {
          loadedCommunicationTaskLabelsKeyRef.current = null;
          communicationsFetchedRef.current = false;
          setCommunications([]);
        }
        return;
      }
      const requestId = communicationRequestIdRef.current + 1;
      communicationRequestIdRef.current = requestId;
      const isLatestCommunicationRequest = () =>
        currentPatientIdRef.current === patientId &&
        communicationRequestIdRef.current === requestId;

      try {
        const res = await listPatientCommunications({
          path: { patient_id: patientId },
        });
        if (!isLatestCommunicationRequest()) {
          return;
        }
        setCommunications(res.data ?? []);
        communicationsFetchedRef.current = true;
        loadedCommunicationTaskLabelsKeyRef.current =
          communicationTaskLabelsKey;
      } catch (err) {
        if (!isLatestCommunicationRequest()) {
          return;
        }
        console.error('Error fetching communications:', err);
        if (!communicationsFetchedRef.current) {
          setCommunications([]);
        }
      } finally {
        // no loading state to clear
      }
    };
    void loadCommunications();
  }, [id, tasksPatientId, communicationTaskIdsKey, tasksRefreshVersion]);

  useEffect(() => {
    if (!patient) return;
    // Clear immediately so stale data from a previous clinic doesn't bleed
    // through while the new fetch is in-flight.
    setClinicians([]);
    let cancelled = false;
    const fetchClinicians = async () => {
      try {
        const response = await getAvailableClinicians({
          query: patient.clinic_id ? { clinic_id: patient.clinic_id } : {},
        });
        if (!cancelled && response?.data) {
          setClinicians(response.data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching clinicians:', err);
          showError(
            'Load Error',
            'Unable to load clinicians. Please try again.'
          );
        }
      }
    };
    void fetchClinicians();
    return () => {
      cancelled = true;
    };
  }, [patient?.clinic_id, showError]);

  /**
   * Physicians from the new ClinicPhysician model. Cached per-clinic and
   * lazily fetched on first lookup for that clinic.
   */
  useEffect(() => {
    const clinicId = patient?.clinic_id;
    if (!clinicId) return;
    if (physiciansByClinic[clinicId] !== undefined) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await listAgencyClinicPhysicians({
          path: { agency_clinic_id: clinicId },
        });
        if (!cancelled) {
          setPhysiciansByClinic(prev => ({
            ...prev,
            [clinicId]: response?.data ?? [],
          }));
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching physicians for clinic:', err);
          setPhysiciansByClinic(prev => ({ ...prev, [clinicId]: [] }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patient?.clinic_id, physiciansByClinic]);

  const formatDate = (dateString: string | null | undefined) => {
    return formatDateToMMDDYYYY(dateString) || 'N/A';
  };

  const formatStatusLabel = useCallback(
    (status: TaskListResponse['status']) =>
      STATUS_DISPLAY_LABELS[status] ??
      status
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, char => char.toUpperCase()),
    []
  );

  const isOrder = useCallback(
    (taskId: string) => {
      const task = tasks.find(t => t.id === taskId);
      return task?.task_type === 'order';
    },
    [tasks]
  );

  const isCommunication = useCallback(
    (taskId: string) => {
      const task = tasks.find(t => t.id === taskId);
      return task?.task_type === 'patient_communication';
    },
    [tasks]
  );

  const isMiscTask = useCallback(
    (taskId: string) => {
      const task = tasks.find(t => t.id === taskId);
      return task?.task_type === 'misc_task';
    },
    [tasks]
  );

  const handleTaskUpdate = async (
    taskId: string,
    updates: {
      assigned_clinician_id?: string | null;
      date?: string;
      scheduled_date?: string;
    },
    successMessage: string
  ) => {
    setUpdatingTaskId(taskId);

    // Reflect the edited fields on the row immediately, derived from the known
    // inputs rather than the differently shaped update response. The row (and
    // calendar) display task_date, so a date change patches it alongside the
    // underlying scheduled_date. Clearing the cached clinician name lets the
    // display recompute from the newly selected clinician.
    const patchEditedTaskLocally = () => {
      const patch: Partial<TaskListResponse> = {};
      if (updates.scheduled_date !== undefined) {
        patch.scheduled_date = updates.scheduled_date;
        patch.task_date = updates.scheduled_date;
      }
      if (updates.date !== undefined) {
        patch.task_date = updates.date;
      }
      if (updates.assigned_clinician_id !== undefined) {
        patch.assigned_clinician_id = updates.assigned_clinician_id;
        patch.assigned_clinician_name = null;
      }
      if (Object.keys(patch).length === 0) return;
      setTasks(prev =>
        prev.map(task => (task.id === taskId ? { ...task, ...patch } : task))
      );
    };

    const refreshTasksAndNotify = async (hasData: boolean) => {
      if (!hasData) return;
      // The update already succeeded, so update the row in place, notify, and
      // then reconcile with a silent refresh. Patching locally keeps the row
      // correct even if that background refresh fails, and never reloads the
      // entire visits section.
      patchEditedTaskLocally();
      showSuccess('Task Updated', successMessage);
      // Re-enable the row's controls before the background refresh so the user
      // is not blocked from further edits while it reconciles. fetchTasks
      // guards concurrent requests on its own via taskRequestIdRef.
      setUpdatingTaskId(null);
      await fetchTasks({ silent: true });
    };

    try {
      if (isCommunication(taskId)) {
        const response = await updatePatientCommunication({
          path: { communication_id: taskId },
          body: {
            assigned_clinician_id: updates.assigned_clinician_id,
            communication_date: updates.date,
          },
        });
        await refreshTasksAndNotify(!!response.data);
      } else if (isMiscTask(taskId)) {
        const response = await updateMiscTask({
          path: { misc_task_id: taskId },
          body: {
            assigned_clinician_id: updates.assigned_clinician_id,
            task_date: updates.date,
          },
        });
        await refreshTasksAndNotify(!!response.data);
      } else if (isOrder(taskId)) {
        const response = await updateOrder({
          path: { order_id: taskId },
          body: {
            assigned_clinician_id: updates.assigned_clinician_id,
            order_date: updates.date,
          },
        });
        await refreshTasksAndNotify(!!response.data);
      } else {
        const response = await updateVisit({
          path: { visit_id: taskId },
          body: {
            assigned_clinician_id: updates.assigned_clinician_id,
            ...(updates.scheduled_date !== undefined
              ? { scheduled_date: updates.scheduled_date }
              : {}),
          },
        });
        await refreshTasksAndNotify(!!response.data);
      }
    } catch (err) {
      console.error('Error updating task:', err);
      // A visit date move returns 422 when the target date falls outside the
      // episode window. Explain that specifically, but only for date moves so
      // an unrelated 422 on another update does not borrow the episode copy.
      if (updates.scheduled_date !== undefined && getHttpStatus(err) === 422) {
        showError(
          'Date Outside Episode',
          'The selected date is outside the episode window.'
        );
      } else {
        showError('Update Failed', 'Failed to update task.');
      }
    } finally {
      setUpdatingTaskId(null);
    }
  };

  const handleAssigneeChange = (taskId: string, clinicianId: string) => {
    handleTaskUpdate(
      taskId,
      { assigned_clinician_id: clinicianId },
      'Assignee updated successfully.'
    );
  };

  const handleTargetDateChange = (
    taskId: string,
    newDate: Date | undefined
  ) => {
    if (!newDate) return;
    const formattedDate = formatDateToISO(newDate);
    if (!formattedDate) return;
    const task = tasks.find(t => t.id === taskId);
    if (task && isVisitTaskRow(task)) {
      // If this visit currently sits on the episode boundary it defines
      // (SOC/REC on start, DC/TRN/DTH on end) and is being moved to a different
      // date, offer to move the episode boundary too. The episode must move
      // first because the reschedule endpoint rejects a date outside the current
      // window, so the offer dialog drives both updates in order.
      const boundary = activeEpisodeBoundaryForVisit(task);
      const boundaryDate =
        boundary === 'start'
          ? task.episode_start_date
          : boundary === 'end'
            ? task.episode_end_date
            : null;
      if (boundary && boundaryDate && formattedDate !== boundaryDate) {
        setEpisodeDateSync({
          boundary,
          episodeId: task.episode_id,
          visitId: task.id,
          newDate: formattedDate,
          currentBoundaryDate: boundaryDate,
          episodeStartDate: task.episode_start_date ?? null,
          episodeEndDate: task.episode_end_date ?? null,
          visitLabel: task.visit_type
            ? OASIS_VISIT_TYPE_LABELS[task.visit_type]
            : undefined,
        });
        return;
      }
      handleTaskUpdate(
        taskId,
        { scheduled_date: formattedDate },
        'Target date updated successfully.'
      );
      return;
    }
    handleTaskUpdate(
      taskId,
      { date: formattedDate },
      'Target date updated successfully.'
    );
  };

  /**
   * Selectable range for a task row's target-date picker. Most tasks are confined
   * to the episode window. A visit sitting on the boundary it defines may move the
   * boundary with it, so that side opens up — but only as far as the episode rules
   * allow (60-day cap, end after start, no overlap with a neighbouring episode),
   * so dates the sync endpoint would reject cannot be picked in the first place.
   */
  const taskTargetDateBounds = useCallback(
    (
      task: TaskListResponse
    ): { minDate: Date | undefined; maxDate: Date | undefined } => {
      const boundary = activeEpisodeBoundaryForVisit(task);
      if (boundary && task.episode_start_date && task.episode_end_date) {
        const bounds = boundaryRescheduleBounds({
          boundary,
          episodeId: task.episode_id,
          episodeStartDate: task.episode_start_date,
          episodeEndDate: task.episode_end_date,
          patientEpisodes: episodes,
        });
        return {
          minDate: parseISOToLocalDate(bounds.minDate),
          maxDate: parseISOToLocalDate(bounds.maxDate),
        };
      }
      return {
        minDate: parseISOToLocalDate(task.episode_start_date),
        maxDate: parseISOToLocalDate(task.episode_end_date),
      };
    },
    [episodes]
  );

  const handleCreateTask = async () => {
    if (!id) return;
    if (!newTaskDate) {
      showError('Validation Error', 'Please select a target date.');
      return;
    }
    const taskDate = formatDateToISO(newTaskDate);
    const boundedTaskDate = clampDateYmdToEpisode(
      taskDate,
      addTaskTargetEpisode
    );
    if (boundedTaskDate !== taskDate) {
      showError(
        'Validation Error',
        'Please select a target date within the episode.'
      );
      setNewTaskDate(dateFromYmd(boundedTaskDate));
      return;
    }

    if (!selectedAddTaskOption || !selectedAddTaskType) {
      showError('Validation Error', 'Please select a task type.');
      return;
    }

    if (selectedAddTaskIsMisc) {
      // Misc tasks need a date and an assignee to be signable; no physician and
      // no notes requirement (the attached document is the record).
      if (!newTaskAssignee) {
        showError('Validation Error', 'Please select a clinician.');
        return;
      }
      setIsCreatingTask(true);
      try {
        const response = await createMiscTask({
          path: { patient_id: id },
          body: {
            ...(addTaskTargetEpisode
              ? { episode_id: addTaskTargetEpisode.id }
              : {}),
            misc_task_type: selectedAddTaskType as MiscTaskTypeId,
            task_date: taskDate,
            assigned_clinician_id: newTaskAssignee,
          },
        });
        if (response.error) {
          console.error('Failed to create misc task:', response.error);
          const serverMessage = getBusinessErrorMessage(response);
          showError('Create Failed', serverMessage ?? 'Failed to create task.');
          return;
        }
        if (response.data) {
          await fetchTasks();
          closeAddTaskDialog();
          showSuccess('Task Created', 'New task added successfully.');
        }
      } catch (err) {
        console.error('Error creating misc task:', err);
        showError('Create Failed', 'Failed to create task.');
      } finally {
        setIsCreatingTask(false);
      }
      return;
    }

    if (selectedAddTaskIsCommunication) {
      if (!newTaskPhysicianId) {
        showError('Validation Error', 'Please select a physician.');
        return;
      }
      // Communications now follow the standard two-signer flow, so they must
      // have an assigned clinician to be signable (ROG-2553). 30-Day Summaries
      // keep their own assignment path and are exempt here.
      if (!selectedAddTaskIsThirtyDaySummary && !newTaskAssignee) {
        showError('Validation Error', 'Please select a clinician.');
        return;
      }
      if (!selectedAddTaskIsThirtyDaySummary && !newCommNotes.trim()) {
        showError('Validation Error', 'Please enter communication details.');
        return;
      }
      setIsCreatingTask(true);
      try {
        const communicationBody = {
          ...(addTaskTargetEpisode
            ? { episode_id: addTaskTargetEpisode.id }
            : {}),
          communication_date: taskDate,
          communication_type: selectedAddTaskType as PatientCommunicationTypeId,
          assigned_clinician_id: selectedAddTaskIsThirtyDaySummary
            ? null
            : newTaskAssignee || null,
          clinic_physician_id: newTaskPhysicianId,
          notes: newCommNotes.trim(),
        };
        const response = await createPatientCommunication({
          path: { patient_id: id },
          body: communicationBody,
        });
        if (response.error) {
          console.error('Failed to create communication:', response.error);
          showError('Create Failed', 'Failed to create communication.');
          return;
        }
        if (response.data) {
          setCommunications(prev => [...prev, response.data!]);
          await fetchTasks();
          closeAddTaskDialog();
          showSuccess(
            'Communication Created',
            'New communication added successfully.'
          );
        }
      } catch (err) {
        console.error('Error creating communication:', err);
        showError('Create Failed', 'Failed to create communication.');
      } finally {
        setIsCreatingTask(false);
      }
      return;
    }

    if (!newTaskAssignee) {
      showError('Validation Error', 'Please select an assignee.');
      return;
    }
    if (selectedAddTaskIsOrder && !newTaskPhysicianId) {
      showError('Validation Error', 'Please select a physician.');
      return;
    }

    setIsCreatingTask(true);
    try {
      const visitOrOrderType = selectedAddTaskType as TaskTypeId;
      const discipline = TASK_TYPE_DISCIPLINE_MAP[visitOrOrderType];

      const response = selectedAddTaskIsOrder
        ? await createOrder({
            body: {
              ...(addTaskTargetEpisode
                ? { episode_id: addTaskTargetEpisode.id }
                : { patient_id: id }),
              order_type: visitOrOrderType as OrderTypeId,
              order_date: taskDate,
              assigned_clinician_id: newTaskAssignee,
              clinic_physician_id: newTaskPhysicianId,
              discipline,
            },
          })
        : await createPatientVisit({
            path: { patient_id: id },
            body: {
              ...(addTaskTargetEpisode
                ? { episode_id: addTaskTargetEpisode.id }
                : {}),
              visit_type: visitOrOrderType as VisitTypeId,
              visit_date: taskDate,
              assigned_clinician_id: newTaskAssignee,
              discipline,
            },
          });

      if (response.error) {
        console.error('Failed to create task:', response.error);
        // Surface a server-provided business message when present (e.g.
        // "Only one CMS-485 can be created per episode."), but never a 5xx body
        // or raw Pydantic 422 field errors: getBusinessErrorMessage returns
        // only a 4xx string / `{ message }` detail, so 5xx strings and 422
        // arrays fall through to the generic message and we don't leak backend
        // `body.<field>` paths.
        const serverMessage = getBusinessErrorMessage(response);
        showError('Create Failed', serverMessage ?? 'Failed to create task.');
        return;
      }
      if (response.data) {
        await fetchTasks();
        closeAddTaskDialog();
        showSuccess('Task Created', 'New task added successfully.');
      }
    } catch (err) {
      console.error('Error creating task:', err);
      showError('Create Failed', 'Failed to create task.');
    } finally {
      setIsCreatingTask(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!deleteTaskId) return;

    setIsDeletingTask(true);
    try {
      if (isCommunication(deleteTaskId)) {
        const response = await deletePatientCommunication({
          path: { communication_id: deleteTaskId },
        });
        if (response.error) {
          console.error('Error deleting communication:', response.error);
          showError('Delete Failed', 'Failed to delete communication.');
          return;
        }
        setTasks(prev => prev.filter(t => t.id !== deleteTaskId));
      } else if (isMiscTask(deleteTaskId)) {
        const response = await deleteMiscTask({
          path: { misc_task_id: deleteTaskId },
        });
        if (response.error) {
          console.error('Error deleting misc task:', response.error);
          showError('Delete Failed', 'Failed to delete task.');
          return;
        }
        setTasks(prev => prev.filter(t => t.id !== deleteTaskId));
      } else if (isOrder(deleteTaskId)) {
        const response = await deleteOrder({
          path: { order_id: deleteTaskId },
        });
        if (response.error) {
          console.error('Error deleting order:', response.error);
          showError('Delete Failed', 'Failed to delete order.');
          return;
        }
        setTasks(prev => prev.filter(t => t.id !== deleteTaskId));
      } else {
        const response = await deleteVisit({
          path: { visit_id: deleteTaskId },
        });
        if (response.error) {
          console.error('Error deleting task:', response.error);
          showError('Delete Failed', 'Failed to delete task.');
          return;
        }
        setTasks(prev => prev.filter(t => t.id !== deleteTaskId));
      }
      setDeleteTaskId(null);
      showSuccess('Task Deleted', 'Task deleted successfully.');
    } catch (err) {
      console.error('Error deleting task:', err);
      showError('Delete Failed', 'Failed to delete task.');
    } finally {
      setIsDeletingTask(false);
    }
  };

  const assignableClinicians = useMemo(
    () => clinicians.filter(c => c.is_assignable_clinician),
    [clinicians]
  );

  const getClinicPhysicians = useMemo(() => {
    return (clinicId: string | null | undefined): ClinicPhysicianResponse[] => {
      if (!clinicId) return [];
      return physiciansByClinic[clinicId] ?? [];
    };
  }, [physiciansByClinic]);

  const getClinicAssignableClinicians = useMemo(() => {
    const dedup = (
      list: typeof assignableClinicians,
      preserveId?: string | null
    ) => {
      const seen = new Set<string>();
      const result: typeof assignableClinicians = [];

      const preserved = preserveId
        ? list.find(c => c.id === preserveId)
        : undefined;
      if (preserved) {
        result.push(preserved);
        seen.add(preserved.user_id);
      }

      for (const c of list) {
        if (c.id === preserveId || seen.has(c.user_id)) continue;
        seen.add(c.user_id);
        result.push(c);
      }
      return result;
    };
    return (
      clinicId: string | null | undefined,
      discipline?: (typeof TASK_TYPE_DISCIPLINE_MAP)[TaskTypeId],
      alwaysIncludeClinicianId?: string | null
    ) => {
      const filtered = assignableClinicians.filter(c => {
        if (alwaysIncludeClinicianId && c.id === alwaysIncludeClinicianId) {
          return true;
        }
        if (clinicId && c.clinic_id !== clinicId) return false;
        return clinicianMatchesDiscipline(c, discipline);
      });
      return dedup(filtered, alwaysIncludeClinicianId);
    };
  }, [assignableClinicians]);

  const getAssignedClinicianName = useCallback(
    (task: TaskListResponse) => {
      if (!task.assigned_clinician_id) return 'Unassigned';

      if (task.assigned_clinician_name) {
        return task.assigned_clinician_name;
      }

      const clinician = getClinicAssignableClinicians(
        task.agency_clinic_id,
        getAssigneeDisciplineForTask(task),
        task.assigned_clinician_id
      ).find(c => c.id === task.assigned_clinician_id);

      return clinician
        ? formatClinicianName(clinician)
        : 'Assigned clinician unavailable';
    },
    [getClinicAssignableClinicians]
  );

  const renderStatusBadge = useCallback(
    (
      task: TaskListResponse,
      lockedTask: ScheduleLockedTask | null,
      className?: string
    ) => (
      <span className='inline-flex items-center gap-1.5'>
        {lockedTask && (
          <span onClick={e => e.stopPropagation()}>
            <LockedTaskIndicator
              taskType={lockedTask.task_type}
              status={lockedTask.status}
            />
          </span>
        )}
        <Badge
          variant={getStatusBadgeVariant(task.status)}
          className={cn('max-w-full', className)}
        >
          <span className='truncate'>{formatStatusLabel(task.status)}</span>
        </Badge>
      </span>
    ),
    [formatStatusLabel]
  );

  // Names that appear more than once (different users, same display name).
  // Deduplicate by user_id first to avoid false positives from the same
  // clinician appearing in multiple clinics.
  const duplicateClinicianNames = useMemo(() => {
    const seen = new Set<string>();
    const deduped = assignableClinicians.filter(c => {
      if (seen.has(c.user_id)) return false;
      seen.add(c.user_id);
      return true;
    });
    const counts = new Map<string, number>();
    for (const c of deduped) {
      const name = formatClinicianName(c);
      counts.set(name, (counts.get(name) || 0) + 1);
    }
    const dupes = new Set<string>();
    for (const [name, count] of counts) {
      if (count > 1) dupes.add(name);
    }
    return dupes;
  }, [assignableClinicians]);

  const formatDuplicateClinicianOptionLabel = useCallback(
    (clinician: ClinicianSelectInput, label: string) => {
      if (duplicateClinicianNames.has(label) && clinician.user_email) {
        return `${label} (${clinician.user_email})`;
      }
      return label;
    },
    [duplicateClinicianNames]
  );

  const allTasks = useMemo(
    () => sortByLocalDate(tasks, 'task_date', 'asc'),
    [tasks]
  );
  const visitTasks = useMemo(
    () => allTasks.filter(task => task.task_type === 'visit'),
    [allTasks]
  );
  const communicationById = useMemo(
    () =>
      new Map(
        communications.map(communication => [communication.id, communication])
      ),
    [communications]
  );

  const effectiveSelectedEpisodeId = useMemo(() => {
    if (!episodes.length) return null;
    if (selectedEpisodeId && episodes.find(e => e.id === selectedEpisodeId))
      return selectedEpisodeId;
    return episodes[0].id;
  }, [episodes, selectedEpisodeId]);

  const selectedEpisode = useMemo(
    () => episodes.find(e => e.id === effectiveSelectedEpisodeId) ?? null,
    [episodes, effectiveSelectedEpisodeId]
  );

  const handleUpdateEpisodeInsurance = useCallback(
    async (insuranceId: string) => {
      if (!effectiveSelectedEpisodeId) return;

      try {
        await updateEpisodeAssignments({
          path: { episode_id: effectiveSelectedEpisodeId },
          body: { patient_insurance_id: insuranceId },
        });

        // Reload episodes to get updated insurance info
        await loadEpisodes();
        showSuccess('Episode insurance updated successfully');
      } catch (error) {
        console.error('Failed to update episode insurance:', error);
        const errorMessage =
          getBusinessErrorMessage(error) ??
          'Failed to update episode insurance';
        showError(errorMessage);
        throw error;
      }
    },
    [effectiveSelectedEpisodeId, loadEpisodes, showSuccess, showError]
  );

  // The episode table does not remount when the active episode changes, so
  // batch selection must be cleared explicitly here — otherwise a selection
  // made under one episode would silently leak into another.
  useEffect(() => {
    setSelectedTaskIds(new Set());
  }, [effectiveSelectedEpisodeId]);

  // ROG-2493: per-30-day-period LUPA thresholds for the selected episode.
  useEffect(() => {
    setLupaPeriods([]);
    if (!effectiveSelectedEpisodeId) {
      return;
    }
    let cancelled = false;
    getEpisodeLupa({
      path: { episode_id: effectiveSelectedEpisodeId },
    })
      .then(response => {
        if (!cancelled) setLupaPeriods(response.data?.periods ?? []);
      })
      .catch(error => {
        // Non-blocking: the supplementary LUPA strip just stays hidden.
        console.error('Failed to load LUPA thresholds', error);
        if (!cancelled) setLupaPeriods([]);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveSelectedEpisodeId]);

  const previousEpisode = useMemo(() => {
    if (episodes.length === 0) return null;
    return episodes.reduce((latest, episode) =>
      latest.end_date > episode.end_date ? latest : episode
    );
  }, [episodes]);

  const currentEpisodeFallback =
    currentEpisodeFallbackState && currentEpisodeFallbackState.patientId === id
      ? currentEpisodeFallbackState.episode
      : null;

  useEffect(() => {
    let cancelled = false;
    const patientId = id;

    const loadCurrentEpisodeFallback = async () => {
      if (!patientId || visitsLoading || tasksPatientId !== patientId) {
        return;
      }
      if (episodes.length > 0) {
        setCurrentEpisodeFallbackState(null);
        return;
      }

      try {
        const response = await getCurrentEpisode({
          path: { patient_id: patientId },
        });
        if (!cancelled && currentPatientIdRef.current === patientId) {
          setCurrentEpisodeFallbackState({
            patientId,
            episode: currentEpisodeToAddTaskTarget(response.data ?? null),
          });
        }
      } catch (error) {
        console.error(
          'Failed to load current episode for Add Task bounds:',
          error
        );
        if (!cancelled && currentPatientIdRef.current === patientId) {
          setCurrentEpisodeFallbackState({ patientId, episode: null });
        }
      }
    };

    void loadCurrentEpisodeFallback();

    return () => {
      cancelled = true;
    };
  }, [episodes.length, id, tasksPatientId, visitsLoading]);

  const taskDerivedLatestEpisode = episodes[0] ?? null;
  const addTaskTargetEpisode = useMemo<AddTaskTargetEpisode | null>(() => {
    if (selectedEpisode) {
      return {
        id: selectedEpisode.id,
        start_date: selectedEpisode.start_date,
        end_date: selectedEpisode.end_date,
        agency_clinic_id:
          tasks.find(t => t.episode_id === selectedEpisode.id)
            ?.agency_clinic_id ?? undefined,
        episode_physician_id:
          tasks.find(t => t.episode_id === selectedEpisode.id)
            ?.episode_physician_id ?? undefined,
      };
    }
    if (taskDerivedLatestEpisode) {
      return {
        id: taskDerivedLatestEpisode.id,
        start_date: taskDerivedLatestEpisode.start_date,
        end_date: taskDerivedLatestEpisode.end_date,
        agency_clinic_id:
          tasks.find(t => t.episode_id === taskDerivedLatestEpisode.id)
            ?.agency_clinic_id ?? undefined,
        episode_physician_id:
          tasks.find(t => t.episode_id === taskDerivedLatestEpisode.id)
            ?.episode_physician_id ?? undefined,
      };
    }
    return currentEpisodeFallback;
  }, [
    currentEpisodeFallback,
    selectedEpisode,
    taskDerivedLatestEpisode,
    tasks,
  ]);

  const addTaskTargetClinicId =
    addTaskTargetEpisode?.agency_clinic_id ?? undefined;

  const selectedAddTaskOption = useMemo(
    () =>
      addTaskOptions.find(option => option.id === selectedAddTaskOptionId) ??
      null,
    [addTaskOptions, selectedAddTaskOptionId]
  );
  const selectedAddTaskIsCommunication =
    selectedAddTaskOption?.taskKind === 'communication';
  const selectedAddTaskIsMisc = selectedAddTaskOption?.taskKind === 'misc';
  const selectedAddTaskType = selectedAddTaskOption?.id as
    | VisitTypeId
    | PatientCommunicationTypeId
    | MiscTaskTypeId
    | undefined;
  // The 30-Day Summary is filled out on its dedicated detail page, so the Add
  // Task modal neither shows nor requires communication notes for it.
  const selectedAddTaskIsThirtyDaySummary =
    selectedAddTaskIsCommunication &&
    selectedAddTaskType === THIRTY_DAY_SUMMARY_COMMUNICATION_TYPE;
  const selectedAddTaskIsOrder =
    !!selectedAddTaskOption &&
    !selectedAddTaskIsCommunication &&
    !selectedAddTaskIsMisc &&
    isOrderType(selectedAddTaskOption.id as TaskTypeId);
  const addTaskAssigneeDiscipline = useMemo(
    () =>
      getAssigneeDisciplineForTaskType(selectedAddTaskType, {
        isOrder: selectedAddTaskIsOrder,
        isCommunication: selectedAddTaskIsCommunication,
        isMisc: selectedAddTaskIsMisc,
      }),
    [
      selectedAddTaskIsCommunication,
      selectedAddTaskIsMisc,
      selectedAddTaskIsOrder,
      selectedAddTaskType,
    ]
  );
  const addTaskAssignableClinicians = useMemo(
    () =>
      getClinicAssignableClinicians(
        addTaskTargetClinicId,
        addTaskAssigneeDiscipline
      ),
    [
      addTaskAssigneeDiscipline,
      addTaskTargetClinicId,
      getClinicAssignableClinicians,
    ]
  );
  const addTaskPhysicianOptions = useMemo(
    () => getClinicPhysicians(addTaskTargetClinicId),
    [addTaskTargetClinicId, getClinicPhysicians]
  );

  useEffect(() => {
    const clinicId = addTaskTargetClinicId;
    if (!clinicId) return;
    if (physiciansByClinic[clinicId] !== undefined) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await listAgencyClinicPhysicians({
          path: { agency_clinic_id: clinicId },
        });
        if (!cancelled) {
          setPhysiciansByClinic(prev => ({
            ...prev,
            [clinicId]: response?.data ?? [],
          }));
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching physicians for episode clinic:', err);
          setPhysiciansByClinic(prev => ({ ...prev, [clinicId]: [] }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addTaskTargetClinicId, physiciansByClinic]);

  useEffect(() => {
    if (!isAddDialogOpen) return;
    if (
      newTaskAssignee &&
      !addTaskAssignableClinicians.some(c => c.id === newTaskAssignee)
    ) {
      setNewTaskAssignee('');
    }
  }, [addTaskAssignableClinicians, isAddDialogOpen, newTaskAssignee]);

  useEffect(() => {
    if (!isAddDialogOpen) return;
    if (!(selectedAddTaskIsOrder || selectedAddTaskIsCommunication)) {
      setNewTaskPhysicianId('');
      return;
    }

    const defaultPhysicianId = addTaskTargetEpisode?.episode_physician_id;
    const defaultPhysicianIsAvailable = addTaskPhysicianOptions.some(
      physician => physician.id === defaultPhysicianId
    );
    setNewTaskPhysicianId(
      defaultPhysicianId && defaultPhysicianIsAvailable
        ? defaultPhysicianId
        : ''
    );
  }, [
    addTaskTargetEpisode?.episode_physician_id,
    addTaskTargetEpisode?.id,
    addTaskPhysicianOptions,
    isAddDialogOpen,
    selectedAddTaskIsCommunication,
    selectedAddTaskIsOrder,
  ]);

  const getBoundedAddTaskDate = useCallback(
    (candidate?: Date): Date | undefined => {
      const candidateYmd = candidate
        ? formatDateToISO(candidate)
        : getTodayInPT();
      const clampedYmd = clampDateYmdToEpisode(
        candidateYmd,
        addTaskTargetEpisode
      );
      return dateFromYmd(clampedYmd);
    },
    [addTaskTargetEpisode]
  );

  const setBoundedNewTaskDate = useCallback(
    (date: Date | undefined) => {
      if (!date) {
        setNewTaskDate(undefined);
        return;
      }
      setNewTaskDate(getBoundedAddTaskDate(date));
    },
    [getBoundedAddTaskDate]
  );

  const resetAddTaskForm = useCallback(
    (initialDate?: Date, initialFilter?: TaskTypeFilterValue) => {
      const filter = initialFilter ?? createDefaultTaskTypeFilter();
      setAddTaskTypeFilter(filter);
      setSelectedAddTaskOptionId(
        getDefaultCreatableTaskTypeOptionId(
          addTaskOptions,
          filter,
          DEFAULT_NEW_TASK_TYPE
        )
      );

      pendingAddTaskFilterRef.current =
        filter !== createDefaultTaskTypeFilter() &&
        filterCreatableTaskTypeOptions(addTaskOptions, filter).length === 0
          ? filter
          : null;

      setNewTaskAssignee('');
      setNewTaskDate(getBoundedAddTaskDate(initialDate));
      setNewCommNotes('');
      setNewTaskPhysicianId('');
    },
    [addTaskOptions, getBoundedAddTaskDate]
  );

  const openAddTaskDialog = useCallback(
    (initialDate?: Date) => {
      resetAddTaskForm(initialDate, tableTaskTypeFilter);
      setIsAddDialogOpen(true);
    },
    [resetAddTaskForm, tableTaskTypeFilter]
  );

  const closeAddTaskDialog = useCallback(() => {
    pendingAddTaskFilterRef.current = null;
    resetAddTaskForm();
    setIsAddDialogOpen(false);
  }, [resetAddTaskForm]);

  useEffect(() => {
    const filter = pendingAddTaskFilterRef.current;
    if (!isAddDialogOpen || !filter || visitTypeOptions.length === 0) {
      return;
    }

    if (filterCreatableTaskTypeOptions(addTaskOptions, filter).length === 0) {
      return;
    }

    setAddTaskTypeFilter(filter);
    setSelectedAddTaskOptionId(
      getDefaultCreatableTaskTypeOptionId(
        addTaskOptions,
        filter,
        DEFAULT_NEW_TASK_TYPE
      )
    );
    pendingAddTaskFilterRef.current = null;
  }, [addTaskOptions, isAddDialogOpen, visitTypeOptions.length]);

  useEffect(() => {
    if (previousPatientIdRef.current === id) {
      return;
    }

    previousPatientIdRef.current = id;
    setTableTaskTypeFilter(createDefaultTaskTypeFilter());
    setSelectedEpisodeId(null);
    closeAddTaskDialog();
  }, [closeAddTaskDialog, id]);

  useEffect(() => {
    if (!isAddDialogOpen || !newTaskDate || !addTaskTargetEpisode) {
      return;
    }
    const currentYmd = formatDateToISO(newTaskDate);
    const clampedYmd = clampDateYmdToEpisode(currentYmd, addTaskTargetEpisode);
    if (clampedYmd !== currentYmd) {
      setNewTaskDate(dateFromYmd(clampedYmd));
    }
  }, [addTaskTargetEpisode, isAddDialogOpen, newTaskDate]);

  // All episodes other than the currently selected one, in the same
  // newest-first order as `episodes`. Surfaced behind a subtle "View other
  // episodes" dropdown since switching episodes is rare.
  const otherEpisodes = useMemo(
    () => episodes.filter(e => e.id !== effectiveSelectedEpisodeId),
    [episodes, effectiveSelectedEpisodeId]
  );

  const episodeStatusBadgeVariant = (
    status: string | null | undefined
  ): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'active':
        return 'default';
      case 'completed':
        return 'secondary';
      case 'cancelled':
      case 'interrupted':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  // Display-only relabel: a cancelled episode reads as "Inactive" (EMR-158). The
  // stored value and badge variant mapping are unchanged.
  const episodeStatusDisplayLabel = (
    status: string | null | undefined
  ): string => (status === 'cancelled' ? 'Inactive' : (status ?? ''));

  const tasksForEpisode = useMemo(
    () =>
      selectedEpisode
        ? sortByLocalDate(
            tasks.filter(t => t.episode_id === selectedEpisode.id),
            'task_date',
            'asc'
          )
        : [],
    [tasks, selectedEpisode]
  );

  const filteredTasksForEpisode = useMemo(
    () => filterTaskRows(tasksForEpisode, tableTaskTypeFilter),
    [tasksForEpisode, tableTaskTypeFilter]
  );

  const documentContextTasks = useMemo(
    () => tasksForEpisode.filter(t => t.task_type !== 'patient_communication'),
    [tasksForEpisode]
  );

  const calendarTasks = useMemo(
    () => filterTaskRows(documentContextTasks, tableTaskTypeFilter),
    [documentContextTasks, tableTaskTypeFilter]
  );
  const getTaskDisplayName = useCallback(
    (task: TaskListResponse) => {
      if (task.task_type !== 'patient_communication') {
        return task.display_name;
      }

      return (
        communicationById.get(task.id)?.communication_type ??
        'Patient Communication'
      );
    },
    [communicationById]
  );

  const getTaskAssigneeLabel = useCallback(
    (task: TaskListResponse) => getAssignedClinicianName(task),
    [getAssignedClinicianName]
  );

  // ===== Batch actions for the episode task table (ROG-2506) =====
  // Resolve from the full `tasks` list (not just the filtered/visible rows) so a
  // cross-discipline selection survives tab switches and shows in the manifest.
  const selectedTasks = useMemo(
    () => tasks.filter(t => selectedTaskIds.has(t.id)),
    [tasks, selectedTaskIds]
  );
  const atSelectionCap = selectedTaskIds.size >= MAX_BATCH_SELECTED;

  const toggleTaskSelected = useCallback((taskId: string) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else if (next.size < MAX_BATCH_SELECTED) {
        next.add(taskId);
      }
      return next;
    });
  }, []);

  const removeTaskFromSelection = useCallback((task: TaskListResponse) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      next.delete(task.id);
      return next;
    });
  }, []);

  const clearTaskSelection = useCallback(
    () => setSelectedTaskIds(new Set()),
    []
  );

  // Header checkbox: checked when every visible row is selected, indeterminate
  // when only some are, unchecked otherwise.
  const selectAllState = useMemo<boolean | 'indeterminate'>(() => {
    if (filteredTasksForEpisode.length === 0) return false;
    const selectedInView = filteredTasksForEpisode.filter(t =>
      selectedTaskIds.has(t.id)
    ).length;
    if (selectedInView === 0) return false;
    if (selectedInView === filteredTasksForEpisode.length) return true;
    return 'indeterminate';
  }, [filteredTasksForEpisode, selectedTaskIds]);

  const handleToggleSelectAll = useCallback(() => {
    setSelectedTaskIds(prev => {
      // Toggle on the checked/indeterminate state (anything selected → clear),
      // so the header still clears when the 30-cap prevented selecting them all.
      const anyInViewSelected = filteredTasksForEpisode.some(t =>
        prev.has(t.id)
      );
      const next = new Set(prev);
      if (anyInViewSelected) {
        filteredTasksForEpisode.forEach(t => next.delete(t.id));
      } else {
        // Select up to the cap, preserving any already-selected out-of-view rows.
        for (const t of filteredTasksForEpisode) {
          if (next.size >= MAX_BATCH_SELECTED) break;
          next.add(t.id);
        }
      }
      return next;
    });
  }, [filteredTasksForEpisode]);

  const runBatchDelete = useCallback(
    async (items: TaskListResponse[]) => {
      const { deletable } = partitionDeletable(items);
      if (deletable.length === 0) return;
      const results = await Promise.allSettled(
        deletable.map(task =>
          task.task_type === 'order'
            ? deleteOrder({ path: { order_id: task.id } })
            : task.task_type === 'misc_task'
              ? deleteMiscTask({ path: { misc_task_id: task.id } })
              : deleteVisit({ path: { visit_id: task.id } })
        )
      );
      const succeededIds: string[] = [];
      const failures: { task: TaskListResponse; code?: string }[] = [];
      results.forEach((result, index) => {
        const task = deletable[index];
        if (result.status === 'fulfilled' && !result.value.error) {
          succeededIds.push(task.id);
        } else {
          const code =
            result.status === 'fulfilled'
              ? (result.value.error as { code?: string } | null | undefined)
                  ?.code
              : undefined;
          failures.push({ task, code: code ?? undefined });
        }
      });
      if (succeededIds.length > 0) {
        const succeeded = new Set(succeededIds);
        setTasks(prev => prev.filter(t => !succeeded.has(t.id)));
        setSelectedTaskIds(prev => {
          const next = new Set(prev);
          succeededIds.forEach(taskId => next.delete(taskId));
          return next;
        });
      }
      if (failures.length === 0) {
        showSuccess(
          'Tasks deleted',
          `${succeededIds.length} task${succeededIds.length === 1 ? '' : 's'} deleted.`
        );
        return;
      }
      const lines = failures
        .slice(0, 3)
        .map(
          f => `${getTaskDisplayName(f.task)}: ${deleteConflictCopy(f.code)}`
        );
      const more =
        failures.length > 3 ? ` …and ${failures.length - 3} more.` : '';
      showError(
        `Couldn't delete ${failures.length} of ${deletable.length}`,
        lines.join('; ') + more
      );
    },
    [getTaskDisplayName, showSuccess, showError]
  );

  const runBatchPrint = useCallback(
    (items: TaskListResponse[], format: 'pdf' | 'zip', choiceKey?: string) => {
      const chosen =
        choiceKey === 'completed' ? partitionNotStarted(items).started : items;
      // Drop task types the endpoint cannot render before the length guard:
      // filtering afterwards would send an empty task list (rejected as a 422)
      // and, for the PDF branch, leave an already-opened blank tab behind.
      const { printable: printSet, skippedUnprintable } =
        partitionPrintable(chosen);
      const episodeId = effectiveSelectedEpisodeId;
      if (!episodeId) return;
      if (printSet.length === 0) {
        showError(
          'Nothing to print',
          'The selected tasks have no printable document.'
        );
        return;
      }
      // Both branches call the endpoint identically except for `format`; only
      // the delivery differs (open in a tab vs. download).
      const callBatchPrint = () =>
        batchPrintEpisodeTasks({
          path: { episode_id: episodeId },
          body: { tasks: toBatchPrintTaskRefs(printSet), format },
        });
      if (format === 'pdf') {
        // exportBatchPdf opens the tab synchronously (within this user gesture),
        // then awaits the fetch and fills it from the returned blob.
        return exportBatchPdf(
          'batch-chart-record',
          async () => {
            const result = await callBatchPrint();
            return result.data
              ? {
                  blob: new Blob([result.data as BlobPart], {
                    type: 'application/pdf',
                  }),
                }
              : null;
          },
          'chart record'
        );
      }
      return (async () => {
        try {
          const result = await callBatchPrint();
          if (result.error || !result.data) {
            console.error('Batch chart-record ZIP failed:', result.error);
            showError(
              'Export failed',
              'Could not generate the chart record. Please try again.'
            );
            return;
          }
          // The client-axios result IS the AxiosResponse (with `error`
          // augmented), so it carries the Content-Disposition filename.
          downloadResponseAsFile(result, 'chart_record.zip');
          const skippedNote =
            skippedUnprintable.length > 0
              ? ` ${skippedUnprintable.length} task${
                  skippedUnprintable.length === 1 ? '' : 's'
                } skipped (no printable document).`
              : '';
          showSuccess(
            'Export complete',
            `${printSet.length} task${printSet.length === 1 ? '' : 's'} exported.${skippedNote}`
          );
        } catch (err) {
          console.error('Batch chart-record ZIP error:', err);
          showError(
            'Export failed',
            'Could not generate the chart record. Please try again.'
          );
        }
      })();
    },
    [effectiveSelectedEpisodeId, exportBatchPdf, showError, showSuccess]
  );

  // Both print actions share this gate: if any selected task is not-started it
  // would render as a blank template, so disclose and let the user choose.
  const notStartedConfirm = useCallback(
    (items: TaskListResponse[]): ConfirmSpec | null => buildPrintConfirm(items),
    []
  );

  const batchActions = useMemo<SelectionAction<TaskListResponse>[]>(
    () => [
      {
        key: 'print-pdf',
        label: 'Print → Combined PDF',
        icon: FileText,
        confirm: notStartedConfirm,
        run: (items, choiceKey) => runBatchPrint(items, 'pdf', choiceKey),
      },
      {
        key: 'print-zip',
        label: 'Print → Download ZIP',
        icon: FileArchive,
        confirm: notStartedConfirm,
        run: (items, choiceKey) => runBatchPrint(items, 'zip', choiceKey),
      },
      {
        key: 'delete',
        label: items =>
          `Delete (${partitionDeletable(items).deletable.length} eligible)`,
        icon: Trash2,
        iconClassName: 'text-destructive',
        destructive: true,
        confirm: items => {
          const { deletable, skippedLocked, skippedComm } =
            partitionDeletable(items);
          const skipped = skippedLocked.length + skippedComm.length;
          if (deletable.length === 0) {
            return {
              title: 'Nothing to delete',
              body: 'None of the selected tasks can be deleted — they are signed, locked, or communications.',
              choices: [{ key: 'ok', label: 'OK' }],
              cancelKeys: ['ok'],
            };
          }
          return {
            title: 'Delete tasks',
            body: `${deletable.length} task${
              deletable.length === 1 ? '' : 's'
            } will be deleted.${
              skipped > 0
                ? ` ${skipped} selected task${
                    skipped === 1 ? '' : 's'
                  } (signed, locked, or communications) will be skipped.`
                : ''
            }`,
            choices: [
              { key: 'cancel', label: 'Cancel', variant: 'outline' },
              { key: 'delete', label: 'Delete', variant: 'destructive' },
            ],
          };
        },
        run: items => runBatchDelete(items),
      },
    ],
    [notStartedConfirm, runBatchPrint, runBatchDelete]
  );

  const selectedTask = useMemo(
    () => tasks.find(t => t.id === selectedVisitId) ?? null,
    [tasks, selectedVisitId]
  );
  const selectedVisitTask = useMemo(
    () => visitTasks.find(t => t.id === selectedVisitId) ?? null,
    [selectedVisitId, visitTasks]
  );
  // The Wounds and Goals tabs share one visit selector, but not every visit type
  // has both sections (ST/transfer/death/supervisory/MSW/aide visits have no
  // wound section; transfer and death visits have no goals section). Offer only
  // the eligible visits per tab, and ignore a selection carried over from the
  // other tab so the tab falls back to its "select a visit" prompt.
  const woundVisitTasks = useMemo(
    () => visitTasks.filter(task => taskSupportsWounds(task)),
    [visitTasks]
  );
  const goalVisitTasks = useMemo(
    () => visitTasks.filter(task => taskShowsGoalsSection(task)),
    [visitTasks]
  );
  const woundVisitTask =
    selectedVisitTask && taskSupportsWounds(selectedVisitTask)
      ? selectedVisitTask
      : null;
  const goalVisitTask =
    selectedVisitTask && taskShowsGoalsSection(selectedVisitTask)
      ? selectedVisitTask
      : null;
  // What the shared Wounds/Goals selector offers and shows as chosen.
  const tabVisitTasks =
    activeTab === 'wounds' ? woundVisitTasks : goalVisitTasks;
  const tabVisitTask = activeTab === 'wounds' ? woundVisitTask : goalVisitTask;
  // A 30-Day Summary communication renders full-page via its dedicated panel;
  // all other patient_communication types render full-page via
  // CommunicationDetailPanel (see the detail-panel switch in the return).
  const selectedTaskIsThirtyDaySummary = useMemo(
    () =>
      !!selectedVisitId &&
      communicationById.get(selectedVisitId)?.communication_type ===
        THIRTY_DAY_SUMMARY_COMMUNICATION_TYPE,
    [selectedVisitId, communicationById]
  );
  const canRenderSelectedTaskDetail =
    activeTab === 'visits' && selectedVisitId && id && selectedTask;

  const handleTaskOpen = useCallback(
    (task: TaskListResponse) => {
      if (!id) return;
      setSelectedVisitId(task.id);
      setActiveTabState('visits');
      navigate(`/patients/${id}/visits/${task.id}`);
    },
    [id, navigate]
  );

  const handleVisitUpdated = useCallback(async () => {
    await Promise.all([loadEpisodes(), fetchTasks()]);
  }, [fetchTasks, loadEpisodes]);

  const handleMedicationOrderCreated = useCallback(
    async (order: OrderResponse) => {
      if (!id) return;
      setSelectedVisitId(order.id);
      setActiveTabState('visits');
      navigate(`/patients/${id}/visits/${order.id}`);
      showSuccess(
        'Medication Order Created',
        'Medication order created successfully.'
      );
      try {
        await fetchTasks();
      } catch (error) {
        console.warn(
          'Failed to refresh patient tasks after medication order',
          error
        );
      }
    },
    [fetchTasks, id, navigate, showSuccess]
  );

  const [medicationCount, setMedicationCount] = useState(0);
  const [allergyCount, setAllergyCount] = useState(0);
  const [documentCount, setDocumentCount] = useState(0);
  const [woundCount, setWoundCount] = useState(0);
  const [authorizationErrorCount, setAuthorizationErrorCount] = useState(0);
  const [authorizationErrors, setAuthorizationErrors] = useState<
    AuthorizationError[]
  >([]);

  // Guards against out-of-order count refreshes: two mutations in quick
  // succession issue overlapping refreshCounts fetches, and without this a slower
  // earlier response could overwrite a newer count. Only the latest request wins.
  const countsRequestRef = useRef(0);
  const refreshCounts = useCallback(async () => {
    if (!id) return;
    const requestId = ++countsRequestRef.current;
    try {
      const [medsRes, allergiesRes, woundsRes, patientDocsRes, intakesRes] =
        await Promise.all([
          getPatientMedications({
            path: { patient_id: id },
            query: { include_discontinued: false },
          }),
          getPatientAllergies({
            path: { patient_id: id },
          }),
          getPatientWounds({
            path: { patient_id: id },
            query: { include_discontinued: false },
          }),
          getPatientDocuments({
            path: { patient_id: id },
            query: { include_task_documents: true },
          }),
          getIntakes({
            query: { patient_id: id },
          }),
        ]);

      const intakeDocumentCount =
        intakesRes?.data?.items?.reduce(
          (total, intake) =>
            total +
            (intake.documents?.filter(d => !d.task_type_tag).length ?? 0),
          0
        ) ?? 0;
      // A newer refresh superseded this one while it was in flight; drop it.
      if (requestId !== countsRequestRef.current) return;
      setMedicationCount(medsRes?.data?.length ?? 0);
      setAllergyCount(allergiesRes?.data?.length ?? 0);
      setWoundCount(woundsRes?.data?.length ?? 0);
      const patientDocCount =
        patientDocsRes?.data?.filter(d => d.document_type !== 'intake')
          .length ?? 0;
      setDocumentCount(patientDocCount + intakeDocumentCount);
    } catch {
      // Counts are non-critical
    }
  }, [id]);

  // Recompute counts on mount, patient change, and tab switch. Exposed as a
  // callback so a task-document upload/delete can refresh the Documents-tab badge
  // immediately, instead of it going stale until the tab is next opened (ROG-2987).
  useEffect(() => {
    void refreshCounts();
  }, [refreshCounts, activeTab]);

  // Refresh the Documents badge the moment a background document upload for this
  // patient completes — patientUploadTick is bumped by every patient- or
  // task-scoped upload. This is decoupled from the uploader's component
  // lifecycle, so it fires even though the paperclip popover closes on upload
  // and the Documents-tab +Add has no direct callback here.
  const patientUploadTick = useStore(
    state => state.patientUploadTick[id ?? ''] ?? 0
  );
  const prevPatientUploadTickRef = useRef(0);
  useEffect(() => {
    if (patientUploadTick !== prevPatientUploadTickRef.current) {
      prevPatientUploadTickRef.current = patientUploadTick;
      // Only the badge is PatientDetail's job here. The Documents list refetches
      // itself via its own tick effect when the tab is mounted; when the tab is
      // closed the ref is null anyway, so a refetch here would be redundant.
      void refreshCounts();
    }
  }, [patientUploadTick, refreshCounts]);

  // Visit-scoped Goals tab badge. Extracted into a hook so the request-id race
  // guard and reset-to-0 paths stay unit-testable; `refreshActiveGoalCount` lets a
  // goal mutation in GoalsTab refresh the badge immediately (ROG-3196).
  const { activeGoalCount, refreshActiveGoalCount } = useActiveGoalCount(
    selectedVisitId,
    tasks,
    activeTab
  );

  const fetchAuthErrors = useCallback(async () => {
    if (!effectiveSelectedEpisodeId) {
      setAuthorizationErrorCount(0);
      setAuthorizationErrors([]);
      return;
    }
    try {
      const res = await getAuthorizationErrors({
        path: { episode_id: effectiveSelectedEpisodeId },
      });
      const errors = res.data?.errors ?? [];
      setAuthorizationErrors(errors);
      setAuthorizationErrorCount(res.data?.count ?? errors.length);
    } catch {
      // Authorization error count is non-critical; silently reset
      setAuthorizationErrorCount(0);
      setAuthorizationErrors([]);
    }
  }, [effectiveSelectedEpisodeId]);

  useEffect(() => {
    // Refresh the badge when the episode changes and whenever task/visit data
    // refreshes (creating/editing/deleting visits changes authorization errors).
    void fetchAuthErrors();
  }, [fetchAuthErrors, tasksRefreshVersion]);

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <div className='flex justify-center mb-4'>
            <LoadingSpinner size='md' />
          </div>
          <p className='text-muted-foreground'>Loading patient details...</p>
        </div>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <AlertTriangle className='h-12 w-12 text-destructive mx-auto mb-4' />
          <h3 className='text-lg font-semibold mb-2'>Error Loading Patient</h3>
          <p className='text-muted-foreground mb-4'>
            {error || 'Patient not found'}
          </p>
          <Button onClick={() => navigate('/patients')} variant='outline'>
            <ArrowLeft className='h-4 w-4' />
            Back to Patients
          </Button>
        </div>
      </div>
    );
  }

  const tabItems: Array<{
    id: PatientTab;
    label: string;
    count?: number;
    isError?: boolean;
    errorMessages?: string[];
  }> = [
    { id: 'visits', label: 'Tasks', count: allTasks.length },
    { id: 'documents', label: 'Documents', count: documentCount },
    {
      id: 'medications',
      label: 'Medications',
      count: medicationCount,
    },
    { id: 'allergies', label: 'Allergies', count: allergyCount },
    { id: 'wounds', label: 'Wounds', count: woundCount },
    { id: 'goals', label: 'Goals', count: activeGoalCount },
    {
      id: 'authorizations',
      label: 'Authorizations',
      count: authorizationErrorCount > 0 ? authorizationErrorCount : undefined,
      isError: true,
      errorMessages: authorizationErrors.map(e => e.message),
    },
    { id: 'events', label: 'Events' },
    { id: 'info', label: 'Patient Info' },
  ];

  // Renders one tab as either a desktop underline tab or a mobile pill. The
  // mobile pills wrap so every tab stays visible on a narrow screen instead of
  // hiding behind a horizontal scroll.
  const renderTabControl = (
    tab: (typeof tabItems)[number],
    variant: 'pill' | 'underline'
  ) => {
    const isActive = activeTab === tab.id;
    const showErrorTooltip = Boolean(tab.isError && tab.errorMessages?.length);
    const className =
      variant === 'pill'
        ? `shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
            isActive
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-muted/70 hover:text-foreground'
          }`
        : `shrink-0 whitespace-nowrap px-3 py-2 text-sm font-medium border-b-2 transition-all ${
            isActive
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border hover:bg-muted/30'
          }`;
    const tabButton = (
      <button
        key={tab.id}
        onClick={() => setActiveTab(tab.id)}
        className={className}
      >
        {tab.label}
        {tab.count != null && tab.count > 0 && (
          <Badge
            variant={tab.isError ? 'destructive' : 'secondary'}
            className={`ml-1.5 text-[10px] px-1.5 py-0 h-4 min-w-4 inline-flex items-center justify-center ${
              variant === 'pill' && isActive && !tab.isError
                ? 'bg-primary-foreground/20 text-primary-foreground'
                : ''
            }`}
          >
            {tab.count}
          </Badge>
        )}
      </button>
    );
    // Surface auth errors when hovering anywhere over the tab, not just the badge.
    return showErrorTooltip ? (
      <TooltipProvider key={tab.id}>
        <Tooltip>
          <TooltipTrigger asChild>{tabButton}</TooltipTrigger>
          <TooltipContent>
            <ul className='list-none space-y-0.5 text-xs'>
              {(tab.errorMessages ?? []).map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      tabButton
    );
  };
  const addEpisodeControl =
    activeTab === 'visits' ? (
      <span className='inline-flex flex-col items-start gap-1'>
        <Button
          size='sm'
          variant='outline'
          disabled={!canAddEpisode}
          onClick={() => setAddEpisodeOpen(true)}
          title={canAddEpisode ? undefined : 'Complete intake first'}
        >
          <Plus className='h-4 w-4' />
          Add Episode
        </Button>
        {!canAddEpisode && (
          <span className='text-xs text-muted-foreground'>
            Complete intake first
          </span>
        )}
      </span>
    ) : null;

  return (
    <div className='bg-background min-h-full flex flex-col'>
      <PatientHeader
        patient={patient}
        insurances={patientInsurances}
        episodePatientInsuranceId={
          selectedEpisode?.patient_insurance_id ?? null
        }
        onEditEpisodeInsurance={() => {
          setIsEpisodeInsuranceEditorOpen(true);
        }}
      />

      {/* Main Content */}
      {canRenderSelectedTaskDetail && selectedTaskIsThirtyDaySummary ? (
        <div className='flex-1 flex flex-col min-h-0'>
          <ThirtyDaySummaryDetailPanel
            communicationId={selectedVisitId}
            onBack={() => {
              setSelectedVisitId(null);
              navigate(`/patients/${id}`);
              fetchTasks();
            }}
          />
        </div>
      ) : canRenderSelectedTaskDetail && selectedTask?.task_type === 'order' ? (
        <div className='flex-1 flex flex-col min-h-0'>
          <OrderDetailPanel
            orderId={selectedVisitId}
            patientId={id}
            onBack={() => {
              setSelectedVisitId(null);
              navigate(`/patients/${id}`);
              fetchTasks();
            }}
          />
        </div>
      ) : canRenderSelectedTaskDetail &&
        selectedTask?.task_type === 'patient_communication' ? (
        <div className='flex-1 flex flex-col min-h-0'>
          <CommunicationDetailPanel
            communicationId={selectedVisitId}
            onBack={() => {
              setSelectedVisitId(null);
              navigate(`/patients/${id}`);
            }}
            onUpdated={() => void fetchTasks()}
          />
        </div>
      ) : canRenderSelectedTaskDetail &&
        selectedTask?.task_type === 'misc_task' ? (
        <div className='flex-1 flex flex-col min-h-0'>
          <MiscTaskDetailPanel
            // Remount per task. The panel guards its own loads against a task
            // switch, but keying it means a new task cannot start from the
            // previous one's state at all.
            key={selectedVisitId}
            miscTaskId={selectedVisitId}
            patientId={id}
            onBack={() => {
              setSelectedVisitId(null);
              navigate(`/patients/${id}`);
            }}
            onUpdated={() => void fetchTasks()}
          />
        </div>
      ) : canRenderSelectedTaskDetail ? (
        <div className='flex-1 flex flex-col min-h-0'>
          <VisitDetailPanel
            visitId={selectedVisitId}
            patientId={id}
            patient={patient}
            onBack={() => {
              setSelectedVisitId(null);
              navigate(`/patients/${id}`);
              fetchTasks();
            }}
            onVisitUpdated={handleVisitUpdated}
            physicians={getClinicPhysicians(patient?.clinic_id)}
          />
        </div>
      ) : (
        <div className='p-2 sm:p-5'>
          <Card className='border-border/70 shadow-sm'>
            <CardHeader>
              <div className='grid grid-cols-1 sm:grid-cols-[1fr_auto] items-start gap-3'>
                <CardDescription className='min-w-0 text-left'>
                  {selectedEpisode ? (
                    <span className='flex flex-col items-start gap-1'>
                      <span className='flex flex-wrap items-center gap-1.5'>
                        <span>
                          Episode: {formatDate(selectedEpisode.start_date)} –{' '}
                          {formatDate(selectedEpisode.end_date)}
                        </span>
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          aria-label='Edit episode'
                          onClick={() => setIsEpisodeEditorOpen(true)}
                        >
                          <Pencil className='h-4 w-4' />
                        </Button>
                        {selectedEpisode.status && (
                          <Badge
                            variant={episodeStatusBadgeVariant(
                              selectedEpisode.status
                            )}
                            className='text-[10px] capitalize leading-normal'
                          >
                            {episodeStatusDisplayLabel(selectedEpisode.status)}
                          </Badge>
                        )}
                        {otherEpisodes.length > 0 && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant='linkMuted' size='sm'>
                                View {otherEpisodes.length} other episode
                                {otherEpisodes.length === 1 ? '' : 's'}
                                <ChevronDown />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align='start'
                              className='min-w-[260px]'
                            >
                              {otherEpisodes.map(ep => (
                                <DropdownMenuItem
                                  key={ep.id}
                                  onClick={() => setSelectedEpisodeId(ep.id)}
                                  className='flex items-center justify-between gap-3'
                                >
                                  <span>
                                    {formatDate(ep.start_date)} –{' '}
                                    {formatDate(ep.end_date)}
                                  </span>
                                  {ep.status && (
                                    <Badge
                                      variant={episodeStatusBadgeVariant(
                                        ep.status
                                      )}
                                      className='text-[10px] capitalize leading-normal'
                                    >
                                      {episodeStatusDisplayLabel(ep.status)}
                                    </Badge>
                                  )}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        {addEpisodeControl}
                        <BillingManagerLink patientId={id!} />
                      </span>
                      {(selectedEpisode.assigned_clinician_name ||
                        selectedEpisode.case_manager_name ||
                        selectedEpisode.patient_insurance_name) && (
                        <span className='text-xs'>
                          {selectedEpisode.assigned_clinician_name && (
                            <>
                              Clinician:{' '}
                              {selectedEpisode.assigned_clinician_name}
                            </>
                          )}
                          {selectedEpisode.assigned_clinician_name &&
                            selectedEpisode.case_manager_name &&
                            ' · '}
                          {selectedEpisode.case_manager_name && (
                            <>
                              Case Manager: {selectedEpisode.case_manager_name}
                            </>
                          )}
                          {(selectedEpisode.assigned_clinician_name ||
                            selectedEpisode.case_manager_name) &&
                            selectedEpisode.patient_insurance_name &&
                            ' · '}
                          {selectedEpisode.patient_insurance_name && (
                            <>
                              Insurance:{' '}
                              {selectedEpisode.patient_insurance_name}
                            </>
                          )}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className='flex flex-wrap items-start gap-2'>
                      <span>Episode tasks and assignments</span>
                      {addEpisodeControl}
                    </span>
                  )}
                </CardDescription>
                {(selectedEpisode || activeTab === 'visits') && (
                  <div className='flex flex-wrap justify-start sm:justify-end gap-2'>
                    {selectedEpisode && (
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={() => setIsEpisodeWorksheetOpen(true)}
                        disabled={!selectedEpisode}
                      >
                        Claim worksheet
                      </Button>
                    )}
                    {activeTab === 'visits' && addTaskTargetEpisode && (
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={() => setIsScheduleVisitsOpen(true)}
                      >
                        <CalendarPlus className='h-4 w-4' />
                        Schedule visits
                      </Button>
                    )}
                    {activeTab === 'visits' && (
                      <Button size='sm' onClick={() => openAddTaskDialog()}>
                        <Plus className='h-4 w-4' />
                        Add Task
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>

            {/* Tab Navigation. Mobile renders wrapping pills so every tab
                stays visible on a narrow screen (no horizontal scroll);
                desktop keeps the underline bar. Rendered one-at-a-time (not
                via CSS hidden) so only one set of tab controls is in the DOM. */}
            {isMobile ? (
              <div className='px-3 pt-1 pb-2.5 border-b border-border/60 flex flex-wrap gap-1.5'>
                {tabItems.map(tab => renderTabControl(tab, 'pill'))}
              </div>
            ) : (
              <div className='border-b border-border/60 px-6 flex items-center gap-1.5 overflow-x-auto'>
                {tabItems.map(tab => renderTabControl(tab, 'underline'))}
              </div>
            )}

            <CardContent className='pt-4'>
              {/* ─── Tasks Tab ─── */}
              {activeTab === 'visits' && (
                <>
                  <VisitsCalendarPanel
                    key={effectiveSelectedEpisodeId ?? 'none'}
                    visits={calendarTasks}
                    episodeStartDate={selectedEpisode?.start_date}
                    episodeEndDate={selectedEpisode?.end_date}
                    onVisitClick={handleTaskOpen}
                    onCreateTaskFromDate={openAddTaskDialog}
                    clinicians={assignableClinicians}
                    duplicateClinicianNames={duplicateClinicianNames}
                    lupaPeriods={lupaPeriods}
                  />
                  <div className='mt-4 border-t border-border/60 pt-4'>
                    {visitsLoading ? (
                      <div className='text-center py-8'>
                        <div className='flex justify-center mb-4'>
                          <LoadingSpinner size='md' />
                        </div>
                        <p className='text-muted-foreground'>
                          Loading tasks...
                        </p>
                      </div>
                    ) : visitsError ? (
                      <div className='py-8 text-center text-muted-foreground'>
                        <AlertTriangle className='h-12 w-12 mx-auto mb-4 opacity-50' />
                        <p>{visitsError}</p>
                      </div>
                    ) : tasksForEpisode.length === 0 ? (
                      <div className='py-8 text-center text-muted-foreground'>
                        <Activity className='h-12 w-12 mx-auto mb-4 opacity-50' />
                        <p>No tasks found for this episode</p>
                      </div>
                    ) : (
                      <div className='overflow-hidden rounded-md border border-border/60 bg-background/40'>
                        <TaskTypeFilter
                          variant='inline'
                          value={tableTaskTypeFilter}
                          onChange={setTableTaskTypeFilter}
                          totalCount={tasksForEpisode.length}
                          filteredCount={filteredTasksForEpisode.length}
                        />
                        <div className='hidden lg:grid lg:grid-cols-[36px_minmax(150px,1fr)_minmax(170px,1fr)_minmax(110px,0.6fr)_minmax(110px,0.6fr)_minmax(200px,1.2fr)_minmax(156px,auto)] items-center gap-3 border-b border-border/60 bg-muted/30 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground'>
                          <Checkbox
                            checkboxSize='sm'
                            aria-label='Select all tasks'
                            checked={selectAllState}
                            onCheckedChange={() => handleToggleSelectAll()}
                          />
                          <span>Task</span>
                          <span>Assignee</span>
                          <span>Visit Date</span>
                          <span>Target Date</span>
                          <span>Status</span>
                          <span className='text-center'>Actions</span>
                        </div>
                        {filteredTasksForEpisode.length === 0 ? (
                          <div className='py-8 text-center text-muted-foreground'>
                            No tasks match these filters.
                          </div>
                        ) : (
                          <div className='divide-y divide-border/60'>
                            {filteredTasksForEpisode.map(task => {
                              const lockedTask = getScheduleLockedTask(task);
                              const isLockedTask = lockedTask !== null;
                              const isCommTask =
                                task.task_type === 'patient_communication';
                              // 30-Day Summaries are assigned from this table
                              // (the detail page no longer carries an assignee
                              // picker), so allow inline assignee editing for
                              // them even though other comms stay read-only.
                              const isThirtyDaySummaryTask =
                                isCommTask &&
                                communicationById.get(task.id)
                                  ?.communication_type ===
                                  THIRTY_DAY_SUMMARY_COMMUNICATION_TYPE;
                              const canEditAssigneeInline =
                                !isLockedTask &&
                                (!isCommTask || isThirtyDaySummaryTask);
                              // 30-Day Summaries expose an editable target date
                              // and a delete action from this table, matching
                              // Visits/Orders, since the episode start date can
                              // shift by a few days. Other communications remain
                              // read-only and non-deletable here.
                              const canEditDateInline =
                                !isLockedTask &&
                                (!isCommTask || isThirtyDaySummaryTask);
                              const canDelete =
                                !isLockedTask &&
                                (!isCommTask || isThirtyDaySummaryTask);
                              const targetDateBounds =
                                taskTargetDateBounds(task);

                              return (
                                <div
                                  key={task.id}
                                  data-testid={`task-row-${task.id}`}
                                  className='group cursor-pointer px-3 py-2 transition-colors hover:bg-muted/35 lg:py-2.5'
                                  onClick={() => handleTaskOpen(task)}
                                >
                                  <div className='grid gap-y-1.5 lg:grid-cols-[36px_minmax(150px,1fr)_minmax(170px,1fr)_minmax(110px,0.6fr)_minmax(110px,0.6fr)_minmax(200px,1.2fr)_minmax(156px,auto)] lg:items-center lg:gap-x-3'>
                                    <div
                                      className='flex items-center'
                                      onClick={e => e.stopPropagation()}
                                    >
                                      <Checkbox
                                        checkboxSize='sm'
                                        aria-label={`Select ${getTaskDisplayName(task)}`}
                                        checked={selectedTaskIds.has(task.id)}
                                        onCheckedChange={() =>
                                          toggleTaskSelected(task.id)
                                        }
                                        disabled={
                                          !selectedTaskIds.has(task.id) &&
                                          atSelectionCap
                                        }
                                      />
                                    </div>
                                    <div className='min-w-0'>
                                      <div className='flex items-center gap-1.5 text-sm font-medium text-foreground'>
                                        <span className='truncate'>
                                          {getTaskDisplayName(task)}
                                        </span>
                                        {task.missed_visit_data && (
                                          <Badge
                                            variant='destructive'
                                            className='shrink-0 text-[10px] px-1.5 py-0'
                                          >
                                            Missed
                                          </Badge>
                                        )}
                                        {isVisitTaskRow(task) &&
                                          task.visit_type &&
                                          supportsActiveDiagnosisPage(
                                            task.visit_type as VisitTypeId
                                          ) &&
                                          task.has_active_diagnosis && (
                                            <Badge
                                              variant='secondary'
                                              className='shrink-0 text-[10px] px-1.5 py-0'
                                              title='Active diagnosis entered'
                                            >
                                              Dx
                                            </Badge>
                                          )}
                                        {/* The Target Date column shows the
                                            scheduled date for visits and the
                                            task date for everything else. */}
                                        <OutsideEpisodeBadge
                                          episodeStartDate={
                                            task.episode_start_date
                                          }
                                          episodeEndDate={task.episode_end_date}
                                          targetDate={
                                            isVisitTaskRow(task)
                                              ? task.scheduled_date
                                              : task.task_date
                                          }
                                          visitDate={
                                            isVisitTaskRow(task)
                                              ? task.task_date
                                              : undefined
                                          }
                                        />
                                      </div>
                                      <div className='mt-0.5 flex items-center gap-2 text-xs text-muted-foreground'>
                                        <span className='lg:hidden'>
                                          {renderStatusBadge(
                                            task,
                                            lockedTask,
                                            'text-[10px]'
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                    <div
                                      onClick={
                                        canEditAssigneeInline
                                          ? e => e.stopPropagation()
                                          : undefined
                                      }
                                      className='grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-x-2 min-w-0 lg:block lg:space-y-1'
                                    >
                                      <span className='whitespace-nowrap text-[11px] font-medium uppercase tracking-wide text-muted-foreground lg:hidden'>
                                        Assignee
                                      </span>
                                      {!canEditAssigneeInline ? (
                                        <div
                                          className={
                                            LOCKED_TASK_ASSIGNEE_TEXT_CLASS
                                          }
                                        >
                                          <span className='truncate'>
                                            {getTaskAssigneeLabel(task)}
                                          </span>
                                        </div>
                                      ) : (
                                        <ClinicianSelect
                                          label=''
                                          name={`task-assignee-${task.id}`}
                                          selectSize='sm'
                                          placeholder='Unassigned'
                                          clinicians={getClinicAssignableClinicians(
                                            task.agency_clinic_id,
                                            getAssigneeDisciplineForTask(task),
                                            task.assigned_clinician_id
                                          )}
                                          value={
                                            task.assigned_clinician_id || ''
                                          }
                                          onChange={event => {
                                            const value = event.target.value;
                                            if (
                                              !value &&
                                              task.assigned_clinician_id
                                            ) {
                                              return;
                                            }
                                            handleAssigneeChange(
                                              task.id,
                                              value
                                            );
                                          }}
                                          disabled={updatingTaskId === task.id}
                                          isClearable={false}
                                          optionLabelFormatter={
                                            formatDuplicateClinicianOptionLabel
                                          }
                                        />
                                      )}
                                    </div>
                                    <div className='grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-x-2 lg:block lg:space-y-1'>
                                      <span className='whitespace-nowrap text-[11px] font-medium uppercase tracking-wide text-muted-foreground lg:hidden'>
                                        Visit Date
                                      </span>
                                      {!isVisitTaskRow(task) ? (
                                        <span className='block text-sm text-muted-foreground'>
                                          —
                                        </span>
                                      ) : (
                                        <span
                                          className={
                                            LOCKED_TASK_DATE_TEXT_CLASS
                                          }
                                        >
                                          {formatDate(task.task_date)}
                                        </span>
                                      )}
                                    </div>
                                    <div
                                      onClick={
                                        canEditDateInline
                                          ? e => e.stopPropagation()
                                          : undefined
                                      }
                                      className='grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-x-2 lg:block lg:space-y-1'
                                    >
                                      <span className='whitespace-nowrap text-[11px] font-medium uppercase tracking-wide text-muted-foreground lg:hidden'>
                                        Target Date
                                      </span>
                                      {!canEditDateInline ? (
                                        <span
                                          className={
                                            isLockedTask
                                              ? LOCKED_TASK_DATE_TEXT_CLASS
                                              : 'block text-sm text-foreground'
                                          }
                                        >
                                          {formatDate(
                                            isVisitTaskRow(task)
                                              ? task.scheduled_date
                                              : task.task_date
                                          )}
                                        </span>
                                      ) : (
                                        <DatePicker
                                          date={
                                            (
                                              isVisitTaskRow(task)
                                                ? task.scheduled_date
                                                : task.task_date
                                            )
                                              ? parseISOToLocalDate(
                                                  isVisitTaskRow(task)
                                                    ? task.scheduled_date
                                                    : task.task_date
                                                )
                                              : undefined
                                          }
                                          onDateChange={(
                                            date: Date | undefined
                                          ) =>
                                            handleTargetDateChange(
                                              task.id,
                                              date
                                            )
                                          }
                                          disabled={updatingTaskId === task.id}
                                          datePickerSize='sm'
                                          className='w-full max-w-[220px]'
                                          minDate={targetDateBounds.minDate}
                                          maxDate={targetDateBounds.maxDate}
                                        />
                                      )}
                                    </div>
                                    <div className='hidden lg:block'>
                                      {renderStatusBadge(
                                        task,
                                        lockedTask,
                                        'text-xs'
                                      )}
                                    </div>
                                    <div className='hidden lg:flex lg:items-center lg:justify-center lg:gap-1'>
                                      {task.notes ? (
                                        <div onClick={e => e.stopPropagation()}>
                                          <Popover>
                                            <PopoverTrigger asChild>
                                              <button className='rounded p-1 hover:bg-muted'>
                                                <MessageSquare className='h-4 w-4 text-blue-500' />
                                              </button>
                                            </PopoverTrigger>
                                            <PopoverContent className='max-w-sm'>
                                              <p className='whitespace-pre-wrap text-sm'>
                                                {task.notes}
                                              </p>
                                            </PopoverContent>
                                          </Popover>
                                        </div>
                                      ) : null}
                                      <div onClick={e => e.stopPropagation()}>
                                        <TaskDocumentsPopover
                                          taskId={task.id}
                                          patientId={id}
                                          initialCount={task.document_count}
                                          readOnly={isDocumentMutationLocked(
                                            task
                                          )}
                                          targetLabel={`${
                                            getTaskDisplayName(task) || 'Task'
                                          }${
                                            patient
                                              ? ` · ${patient.first_name} ${patient.last_name}`
                                              : ''
                                          }`}
                                          onDocumentsChanged={() => {
                                            void fetchTasks({ silent: true });
                                            void refreshCounts();
                                          }}
                                        />
                                      </div>
                                      {hasPrintView(task) && (
                                        <div onClick={e => e.stopPropagation()}>
                                          <Button
                                            variant='ghost'
                                            size='sm'
                                            aria-label={`Print view for ${getTaskDisplayName(
                                              task
                                            )}`}
                                            disabled={
                                              printingTaskId === task.id
                                            }
                                            onClick={() => void printTask(task)}
                                            className='h-8 w-8 p-0'
                                          >
                                            {printingTaskId === task.id ? (
                                              <div className='animate-spin rounded-full h-3 w-3 border-b-2 border-current' />
                                            ) : (
                                              <Printer className='h-4 w-4' />
                                            )}
                                          </Button>
                                        </div>
                                      )}
                                      {canDelete && (
                                        <div onClick={e => e.stopPropagation()}>
                                          <Button
                                            variant='ghost'
                                            size='sm'
                                            aria-label={`Delete ${task.display_name}`}
                                            onClick={() =>
                                              setDeleteTaskId(task.id)
                                            }
                                            disabled={
                                              updatingTaskId === task.id
                                            }
                                            className='h-8 w-8 p-0 opacity-70 transition-opacity group-hover:opacity-100'
                                          >
                                            <Trash2 className='h-4 w-4 text-destructive' />
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <SelectionActionBar
                          items={selectedTasks}
                          onClear={clearTaskSelection}
                          onRemoveItem={removeTaskFromSelection}
                          actions={batchActions}
                          getItemKey={task => task.id}
                          getItemLabel={task =>
                            getTaskDisplayName(task) || 'Task'
                          }
                          getItemGroup={taskManifestGroup}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Documents Tab */}
              {activeTab === 'documents' && (
                <PatientDocumentsTab
                  ref={documentsRef}
                  patientId={id!}
                  patientLabel={
                    patient
                      ? `${patient.first_name} ${patient.last_name}`
                      : undefined
                  }
                  documentContextTasks={documentContextTasks}
                />
              )}

              {/* ─── Medications Tab ─── */}
              {activeTab === 'medications' && (
                <PatientMedications
                  patientId={id!}
                  episodeId={selectedEpisode?.id}
                  visitId={selectedVisitTask?.id}
                  visitStatus={selectedVisitTask?.status}
                  visitSelector={
                    <div className='flex w-full flex-wrap items-center gap-2 sm:w-auto'>
                      <Select
                        value={selectedVisitTask?.id ?? ''}
                        onValueChange={v => setSelectedVisitId(v || null)}
                      >
                        <SelectTrigger
                          selectSize='sm'
                          className='w-full sm:w-[300px]'
                        >
                          <SelectValue placeholder='Select visit for MAR' />
                        </SelectTrigger>
                        <SelectContent>
                          {[...visitTasks]
                            .sort(
                              (a, b) =>
                                (parseISOToLocalDate(
                                  b.task_date ?? b.scheduled_date
                                )?.getTime() ?? 0) -
                                (parseISOToLocalDate(
                                  a.task_date ?? a.scheduled_date
                                )?.getTime() ?? 0)
                            )
                            .map(task => (
                              <SelectItem key={task.id} value={task.id}>
                                {task.display_name} -{' '}
                                {formatDate(
                                  task.task_date ?? task.scheduled_date
                                )}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {selectedVisitTask ? (
                        <button
                          onClick={() => setSelectedVisitId(null)}
                          className='text-xs text-muted-foreground underline hover:text-foreground'
                        >
                          Clear
                        </button>
                      ) : (
                        <span className='text-xs text-muted-foreground'>
                          Select a visit to record administrations
                        </span>
                      )}
                    </div>
                  }
                  onMedicationOrderCreated={handleMedicationOrderCreated}
                  onDocumentCreated={() =>
                    documentsRef.current?.refreshDocuments()
                  }
                  onMedicationsChanged={() => void refreshCounts()}
                />
              )}

              {/* Allergies Tab */}
              {activeTab === 'allergies' && (
                <PatientAllergies
                  patient={patient}
                  onAllergiesChanged={() => void refreshCounts()}
                />
              )}

              {/* Visit-scoped context (Wounds + Goals) */}
              {(activeTab === 'wounds' || activeTab === 'goals') && (
                <div className='mb-4 mt-1'>
                  {/* With no eligible visit there is nothing to pick, so the tab
                      body carries the single explanatory message instead. */}
                  {tabVisitTasks.length > 0 && (
                    <>
                      <div className='flex items-center gap-2'>
                        <Select
                          value={tabVisitTask?.id ?? ''}
                          onValueChange={v => setSelectedVisitId(v || null)}
                        >
                          <SelectTrigger
                            selectSize='sm'
                            className={`w-full sm:w-[360px] ${
                              !tabVisitTask
                                ? 'border-destructive/60 text-destructive'
                                : ''
                            }`}
                          >
                            <SelectValue placeholder='Select visit' />
                          </SelectTrigger>
                          <SelectContent>
                            {[...tabVisitTasks]
                              .sort(
                                (a, b) =>
                                  new Date(
                                    b.task_date ?? b.scheduled_date
                                  ).getTime() -
                                  new Date(
                                    a.task_date ?? a.scheduled_date
                                  ).getTime()
                              )
                              .map(task => (
                                <SelectItem key={task.id} value={task.id}>
                                  {task.display_name} -{' '}
                                  {formatDate(
                                    task.task_date ?? task.scheduled_date
                                  )}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        {tabVisitTask && (
                          <button
                            onClick={() => setSelectedVisitId(null)}
                            className='text-xs text-muted-foreground underline hover:text-foreground'
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      {!tabVisitTask && (
                        <p className='mt-2 inline-flex items-center gap-1.5 text-xs text-destructive'>
                          <AlertCircle className='h-3.5 w-3.5' />
                          Select a visit to enable{' '}
                          {activeTab === 'wounds' ? 'wound actions' : 'goals'}.
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Wounds Tab */}
              {activeTab === 'wounds' && (
                <PatientWounds
                  patientId={id!}
                  visitId={woundVisitTask?.id}
                  visitStatus={woundVisitTask?.status}
                  // Unfiltered on purpose: this is the lookup the assessments
                  // drawer uses to name the visit each assessment was recorded
                  // on, including visits whose type no longer has a wound
                  // section. The picker above is what gets filtered.
                  visits={visitTasks}
                  onWoundsChanged={() => void refreshCounts()}
                  // Wounds are patient-scoped history, so keep the list readable
                  // even when no visit can serve as wound context.
                  noVisitContextReason={
                    woundVisitTasks.length === 0
                      ? 'No visits on this patient have a wounds section. Existing wounds can be corrected or removed here, but not added or assessed.'
                      : undefined
                  }
                />
              )}

              {/* Goals Tab */}
              {activeTab === 'goals' &&
                (goalVisitTask ? (
                  <div className='h-[calc(100vh-10rem)]'>
                    <GoalsTab
                      open={true}
                      onClose={() => {}}
                      patientFirstName={patient.first_name}
                      patientLastName={patient.last_name}
                      visit={goalVisitTask}
                      inline={true}
                      onGoalsChanged={() => void refreshActiveGoalCount()}
                    />
                  </div>
                ) : (
                  <div className='flex flex-col items-center justify-center py-12 text-muted-foreground'>
                    <Target className='h-12 w-12 mb-4 opacity-30' />
                    <p className='text-base font-medium'>
                      {goalVisitTasks.length === 0
                        ? 'No visits on this patient have a goals section.'
                        : 'Select a visit to get started.'}
                    </p>
                  </div>
                ))}

              {/* ─── Authorizations Tab ─── */}
              {activeTab === 'authorizations' && (
                <AuthorizationsTab
                  patientId={id!}
                  episodeId={effectiveSelectedEpisodeId}
                  episodeStartDate={selectedEpisode?.start_date}
                  episodeEndDate={selectedEpisode?.end_date}
                  defaultInsuranceId={selectedEpisode?.patient_insurance_id}
                  onErrorsChanged={fetchAuthErrors}
                />
              )}

              {/* ─── Events Tab ─── */}
              {activeTab === 'events' && (
                <EventsTab
                  patientId={id!}
                  episodeId={effectiveSelectedEpisodeId}
                  episodeStartDate={selectedEpisode?.start_date}
                  episodeEndDate={selectedEpisode?.end_date}
                  episodes={episodes}
                  clinicians={assignableClinicians}
                  physicians={getClinicPhysicians(patient?.clinic_id)}
                />
              )}

              {/* ─── Patient Info Tab ─── */}
              {activeTab === 'info' && (
                <PatientInfoTab
                  patient={patient}
                  onPatientUpdated={updated => setPatient(updated)}
                  onAddInsurance={() => {
                    setEditingInsuranceId(null);
                    setInsurancePopupOpen(true);
                  }}
                  onEditInsurance={insuranceId => {
                    setEditingInsuranceId(insuranceId);
                    setInsurancePopupOpen(true);
                  }}
                  insuranceRefreshTrigger={insuranceRefreshTrigger}
                  highlightSection={
                    activeTab === 'info' && sectionParam === 'insurance'
                      ? 'insurance'
                      : undefined
                  }
                />
              )}
            </CardContent>
          </Card>
          <AddEpisodeModal
            open={addEpisodeOpen}
            onClose={() => setAddEpisodeOpen(false)}
            patientId={id!}
            clinicId={patient.clinic_id ?? ''}
            previousEpisode={previousEpisode}
            existingEpisodes={episodes}
            onCreated={newEpisode => {
              setEpisodes(prev => [
                newEpisode,
                ...prev.filter(episode => episode.id !== newEpisode.id),
              ]);
              setSelectedEpisodeId(newEpisode.id);
              void loadEpisodes();
            }}
          />
          <PatientInsuranceFormDialog
            open={insurancePopupOpen}
            onClose={() => {
              setInsurancePopupOpen(false);
              setEditingInsuranceId(null);
              setInsurancesLoading(true);
              void listPatientInsurances({
                path: { patient_id: id! },
              })
                .then(res => {
                  if (res.data) {
                    setPatientInsurances(res.data);
                    setInsuranceRefreshTrigger(prev => prev + 1);
                  }
                })
                .catch(err => {
                  console.error('Failed to load patient insurances:', err);
                })
                .finally(() => {
                  setInsurancesLoading(false);
                });
            }}
            patientId={id!}
            clinicId={patient.clinic_id}
            patient={patient}
            editingInsuranceId={editingInsuranceId}
          />
        </div>
      )}

      {/* Schedule Visits Dialog (bulk create across multiple dates) */}
      {addTaskTargetEpisode && (
        <ScheduleVisitsDialog
          open={isScheduleVisitsOpen}
          onOpenChange={setIsScheduleVisitsOpen}
          patientId={id!}
          episodeId={addTaskTargetEpisode.id}
          episodeStartDate={addTaskTargetEpisode.start_date}
          episodeEndDate={addTaskTargetEpisode.end_date}
          visitTypeOptions={visitTypeOptions}
          clinicians={assignableClinicians}
          duplicateClinicianNames={duplicateClinicianNames}
          onCreated={fetchTasks}
        />
      )}

      {/* Add Task Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onOpenChange={open => {
          if (open) {
            openAddTaskDialog();
            return;
          }
          closeAddTaskDialog();
        }}
      >
        <DialogContent className='w-[calc(100vw-2rem)] overflow-visible sm:max-w-3xl'>
          <DialogHeader>
            <DialogTitle>Add New Task</DialogTitle>
            <DialogDescription className='sr-only'>
              Select a task type, assignee, and date for this episode.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4'>
            <div>
              <label className='text-sm font-medium'>Task Type</label>
              <div className='mt-1'>
                <TaskTypeCombobox
                  options={addTaskOptions}
                  value={selectedAddTaskOptionId}
                  onValueChange={option => {
                    setSelectedAddTaskOptionId(option.id);
                    setNewCommNotes('');
                  }}
                  filter={addTaskTypeFilter}
                  onFilterChange={setAddTaskTypeFilter}
                  modal={true}
                  disabled={isCreatingTask}
                />
              </div>
            </div>

            {!selectedAddTaskIsThirtyDaySummary && (
              <div>
                <DropdownField
                  label='Assignee'
                  htmlFor='add-task-assignee'
                  variant='formSelect'
                  sortOptions
                  placeholder='Select assignee'
                  options={cliniciansToDropdownOptions(
                    addTaskAssignableClinicians,
                    duplicateClinicianNames
                  )}
                  value={newTaskAssignee}
                  onChange={setNewTaskAssignee}
                  renderOption={option => {
                    const o = option as ClinicianDropdownOption;
                    return (
                      <>
                        {o.label}
                        {o.isDup && (
                          <span className='ml-1 text-xs text-muted-foreground'>
                            ({o.user_email})
                          </span>
                        )}
                      </>
                    );
                  }}
                />
              </div>
            )}

            {(selectedAddTaskIsOrder || selectedAddTaskIsCommunication) && (
              <div>
                <DropdownField
                  label='Physician'
                  htmlFor='add-task-physician'
                  variant='formSelect'
                  sortOptions
                  placeholder='Select physician'
                  options={addTaskPhysicianOptions.map(physician => ({
                    value: physician.id,
                    label: formatPhysicianName(physician),
                  }))}
                  value={newTaskPhysicianId}
                  onChange={setNewTaskPhysicianId}
                  disabled={isCreatingTask}
                />
              </div>
            )}

            <div>
              <label className='text-sm font-medium'>
                {selectedAddTaskIsCommunication || selectedAddTaskIsMisc
                  ? 'Date'
                  : 'Target Date'}
              </label>
              <div className='mt-1'>
                <DatePicker
                  date={newTaskDate}
                  onDateChange={setBoundedNewTaskDate}
                  minDate={
                    addTaskTargetEpisode?.start_date
                      ? parseISOToLocalDate(addTaskTargetEpisode.start_date)
                      : undefined
                  }
                  maxDate={
                    addTaskTargetEpisode?.end_date
                      ? parseISOToLocalDate(addTaskTargetEpisode.end_date)
                      : undefined
                  }
                />
              </div>
            </div>

            {selectedAddTaskIsCommunication &&
              !selectedAddTaskIsThirtyDaySummary && (
                <div>
                  <label className='text-sm font-medium'>
                    Communication Details
                  </label>
                  <textarea
                    value={newCommNotes}
                    onChange={e => setNewCommNotes(e.target.value)}
                    placeholder='Describe the communication and any follow-up actions…'
                    className='mt-1 w-full min-h-[100px] resize-y rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                    disabled={isCreatingTask}
                  />
                </div>
              )}
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={closeAddTaskDialog}
              disabled={isCreatingTask}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTask}
              disabled={
                isCreatingTask ||
                !selectedAddTaskOption ||
                (!selectedAddTaskIsCommunication && !newTaskAssignee) ||
                ((selectedAddTaskIsOrder || selectedAddTaskIsCommunication) &&
                  !newTaskPhysicianId) ||
                (selectedAddTaskIsCommunication &&
                  !selectedAddTaskIsThirtyDaySummary &&
                  !newCommNotes.trim())
              }
            >
              {isCreatingTask ? 'Creating...' : 'Add Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteTaskId}
        onOpenChange={open => !open && setDeleteTaskId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Task</DialogTitle>
          </DialogHeader>
          <p className='text-sm text-muted-foreground'>
            Are you sure you want to delete this task? This action cannot be
            undone.
          </p>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setDeleteTaskId(null)}
              disabled={isDeletingTask}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleDeleteTask}
              disabled={isDeletingTask}
            >
              {isDeletingTask ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Episode Dialog */}
      <Dialog open={isEpisodeEditorOpen} onOpenChange={setIsEpisodeEditorOpen}>
        <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>Edit Episode</DialogTitle>
            <DialogDescription className='sr-only'>
              Edit episode information.
            </DialogDescription>
          </DialogHeader>
          {selectedEpisode && (
            <EpisodeEditor
              episodeId={selectedEpisode.id}
              onCancel={() => setIsEpisodeEditorOpen(false)}
              onSaved={() => {
                setIsEpisodeEditorOpen(false);
                void loadEpisodes();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {episodeDateSync && (
        <EpisodeDateSyncDialog
          open
          boundary={episodeDateSync.boundary}
          episodeId={episodeDateSync.episodeId}
          visitId={episodeDateSync.visitId}
          newDate={episodeDateSync.newDate}
          currentBoundaryDate={episodeDateSync.currentBoundaryDate}
          episodeStartDate={episodeDateSync.episodeStartDate}
          episodeEndDate={episodeDateSync.episodeEndDate}
          visitLabel={episodeDateSync.visitLabel}
          onClose={() => setEpisodeDateSync(null)}
          onCompleted={() => {
            void fetchTasks({ silent: true });
            void loadEpisodes();
          }}
          onUpdateVisitOnly={() => {
            // Move only the visit, leaving the episode dates untouched. The
            // standard reschedule path enforces that the date stays inside the
            // episode window (the dialog only offers this when it does).
            const { visitId, newDate } = episodeDateSync;
            setEpisodeDateSync(null);
            void handleTaskUpdate(
              visitId,
              { scheduled_date: newDate },
              'Target date updated successfully.'
            );
          }}
        />
      )}

      <EpisodeInsuranceSelector
        open={isEpisodeInsuranceEditorOpen}
        onClose={() => setIsEpisodeInsuranceEditorOpen(false)}
        onConfirm={handleUpdateEpisodeInsurance}
        patientInsurances={patientInsurances}
        currentInsuranceId={selectedEpisode?.patient_insurance_id}
        insurancesLoading={insurancesLoading}
        patientId={id}
      />

      {/* Episode Worksheet Editor */}
      {isEpisodeWorksheetOpen && selectedEpisode && (
        <EpisodeWorksheetEditor
          episodeId={selectedEpisode.id}
          onClose={() => setIsEpisodeWorksheetOpen(false)}
        />
      )}
    </div>
  );
};

export default PatientDetail;
